// Reports (Blueprint §2 screen 6) — the approved visual spec rendered off the
// METRICS registry.
//
// The rule this file obeys above all others: it computes NOTHING. Every number,
// every series, every table row comes from src/metrics/registry.js via
// data/reports.js aggregate(). The view's job is layout, honesty affordances
// (caveat tooltips, NEEDS-FIELD states, suppressed deltas) and interaction. If a
// number needs changing, it changes in the registry and the screen, the CSV and
// (Phase 5B) the deck all change together — that is R-022.
//
// Structure, following the approved mock:
//   sticky filter bar  — period presets · clock · compare · slice chips · tier
//   exec summary band  — RULE-BASED narrative (NO AI), clauses guard their inputs
//   KPI band           — 12 tiles, value + delta (only when comparing) + sparkline
//   10 sections        — Growth · Access & SHA · Adherence · Retention · Safety ·
//                        Reachability · Financing · Geography · Team · Data quality
//   deck builder       — per-tile checkboxes feeding the Phase-5B export
//
// Charts use the VENDORED Chart.js behind a load guard; sparklines are hand-rolled
// inline SVG (a 64×20 spark does not justify a chart engine). Every chart instance
// is held in a module Map and destroyed before its canvas is reused — leaking
// Chart instances across re-renders is how the legacy page ended up with ghost
// tooltips after a filter click.

import { store, SLICES } from '../state/store.js';
import { esc, csvSafe } from '../ui/esc.js';
import { toast } from '../ui/toast.js';
import { register } from '../ui/actions.js';
import { netErr } from '../errors.js';
import { registerView, reportArea } from './registry.js';
import { progDotHTML, pillLabel } from '../programmes.js';
import { openWorkspace } from './workspace.js';
import { trapStep, FOCUSABLE } from '../ui/modal.js';
import {
  PERIODS,
  CLOCKS,
  SLICE_DIMS,
  defaultFilters,
  emptySlices,
  applyFilters,
  aggregate,
  comparePrevious,
  sliceValueCounts,
  periodRange,
  fetchReportData,
  reportsRaw,
  reportsLoaded,
  reportsError,
  reportsTruncated,
} from '../data/reports.js';
import {
  SECTIONS,
  REPORT_AREAS,
  REPORT_AREA_KEYS,
  TIER,
  DATA_STATE,
  GOOD,
  metricsFor,
  kpiMetrics,
  getMetric,
  metricDelta,
  metricRag,
  monthlySeries,
  fmtNum,
  fmtPct,
  fmtMoney,
  fmtDays,
} from '../metrics/registry.js';
import {
  buildSlidePlan,
  renderDeck,
  deckChartMetrics,
  deckFileName,
  getPptxGen,
  chartTakeaway,
} from '../data/deck.js';

// ─── Module state ──────────────────────────────────────────────────────────

let _filters = defaultFilters();
/** The active pivot tab (report area). Deep-linked as #/reports/<key>. */
let _area = 'overview';
let _wired = false;
/** @type {Map<string, any>} live Chart.js instances, keyed by canvas id. */
const _charts = new Map();
/** @type {Set<string>} metric ids ticked for the deck (Phase 5B export). */
let _deck = new Set();
/** @type {Record<string, string>} dim key → the active (arrow-focused) option value. */
let _activeOpt = /** @type {Record<string, string>} */ ({});
/** The Power BI-style filters panel (right dock / mobile sheet) is open. */
let _panelOpen = false;
/** The Period dropdown popover is open. */
let _periodOpen = false;
/** @type {Set<string>} slice dims whose accordion group is expanded in the panel. */
let _openGroups = new Set();
/** Latched so the "charts unavailable" toast warns once per visit, not per click. */
let _chartsWarned = false;
/**
 * Sign-in audit for the Operations tab. Admin-only, and null for everyone else
 * so a non-admin render cannot even reach the markup. `{rows, error}` once a
 * load has been attempted — an error is kept and EXPLAINED rather than shown as
 * an empty panel, which would read as "nobody signed in".
 * @type {{rows: any[], error: any}|null}
 */
/** @type {{data: any, error: any}|null} admin-only training performance (null for non-admins) */

/** @returns {any} */
function doc() {
  return /** @type {any} */ (globalThis).document || null;
}

/** @returns {boolean} */
function mounted() {
  const d = doc();
  return !!(d && d.getElementById('rpt-root'));
}

/** The vendored Chart.js global, or null when the script did not load. @returns {any} */
export function getChart() {
  const C = /** @type {any} */ (globalThis).Chart;
  return C && typeof C === 'function' ? C : null;
}

/** The programme catalogue as a list (for per-programme metrics). @returns {any[]} */
function programmeList() {
  const p = store.get(SLICES.PROGRAMMES) || {};
  const byCode = p.byCode || {};
  return Object.keys(byCode).map((k) => byCode[k]);
}

// ─── Small presentational helpers ──────────────────────────────────────────

/** @param {string} paths @param {number} [size] @returns {string} */
function ic(paths, size = 14) {
  return (
    `<svg class="ic" viewBox="0 0 24 24" width="${size}" height="${size}" fill="none" stroke="currentColor" ` +
    'stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
    paths +
    '</svg>'
  );
}
const IC_DOWN = '<path d="M12 3v13"/><path d="M7 12l5 5 5-5"/><path d="M5 21h14"/>';
const IC_ARROW = '<path d="M5 12h14"/><path d="M13 6l6 6-6 6"/>';
const IC_CHEVRON = '<path d="M6 9l6 6 6-6"/>';
const IC_FILTER = '<path d="M3 5h18l-7 8v5l-4 2v-7z"/>';
const IC_COMPARE = '<path d="M4 8h13l-3-3"/><path d="M20 16H7l3 3"/>';
const IC_X = '<path d="M18 6L6 18"/><path d="M6 6l12 12"/>';
const IC_LOCK = '<rect x="5" y="11" width="14" height="9" rx="2"/><path d="M8 11V8a4 4 0 0 1 8 0v3"/>';

// Per-area presentation: the pivot-tab / scorecard icon (hand-rolled SVG paths,
// drawn by ic()) and the "who reads this" subtitle that recovers the audience the
// journey grouping deliberately does not put in the tab label. Membership itself
// lives in REPORT_AREAS (metrics registry) — this is view chrome only.
/** @type {Record<string, {icon: string, sub: string}>} */
const AREA_META = {
  overview: {
    icon: '<rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>',
    sub: '',
  },
  enrolment: {
    icon: '<circle cx="9" cy="7" r="3.2"/><path d="M4 21v-1a5 5 0 0 1 5-5h1"/><path d="M17 11v6"/><path d="M14 14h6"/>',
    sub: "Who's coming in · programme leads",
  },
  access: {
    icon: '<path d="M17 8V6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2"/><path d="M21 12h-6a2 2 0 0 0 0 4h6z"/>',
    sub: 'Getting funded and treated · SHA / finance liaison',
  },
  treatment: {
    icon: '<path d="M10.5 20.5a5 5 0 0 1-7-7l6-6a5 5 0 0 1 7 7z"/><path d="M8 8l7 7"/>',
    sub: 'On therapy, safely · clinical and PV leads',
  },
  retention: {
    icon: '<circle cx="8.5" cy="8" r="3"/><path d="M3 21v-1a5.5 5.5 0 0 1 11 0v1"/><circle cx="17.5" cy="9.5" r="2.3"/><path d="M16 21v-1a4.5 4.5 0 0 1 5-4.4"/>',
    sub: 'Keeping and reaching everyone, everywhere · coordinators',
  },
  operations: {
    icon: '<path d="M4 7h16"/><path d="M4 12h16"/><path d="M4 17h16"/><circle cx="9" cy="7" r="2"/><circle cx="15" cy="12" r="2"/><circle cx="7" cy="17" r="2"/>',
    sub: 'Running the programme · ops, finance and data',
  },
};

// The 1–3 headline metric ids each area's Overview scorecard shows. Every id is
// resolved through getMetric() before use, so a typo drops the stat rather than
// crashing the card; a needs-field id would render its honest '—', never a 0.
/** @type {Record<string, string[]>} */
const SCORECARD = {
  enrolment: ['enrolled', 'active', 'on_treatment'],
  access: ['sha_approved', 'sha_pending', 'time_to_treatment'],
  treatment: ['refills_on_time', 'serious_ae_mtd'],
  retention: ['ltfu', 'contactable'],
  operations: ['cost_per_patient', 'field_completeness'],
};

// Short labels for the scorecard mini-stats — the full metric label is too long
// for a three-across cluster. Falls back to the metric's own label.
/** @type {Record<string, string>} */
const SC_LABEL = {
  enrolled: 'Enrolled',
  active: 'Active',
  on_treatment: 'On treat.',
  sha_approved: 'Approved',
  sha_pending: 'Pending',
  time_to_treatment: 'To treat.',
  refills_on_time: 'On-time',
  serious_ae_mtd: 'Serious AE',
  ltfu: 'Lost f/up',
  contactable: 'Contactable',
  cost_per_patient: 'KSh / pt.',
  field_completeness: 'Fields',
};

/**
 * Tiny inline SVG sparkline (legacy rptSpark). Returns '' for a series with
 * nothing to show — a flat line of zeros is noise, not information.
 * @param {number[]} vals
 * @param {string} [color]
 * @returns {string}
 */
export function sparkline(vals, color) {
  const v = (vals || []).filter((x) => typeof x === 'number' && isFinite(x));
  if (v.length < 2 || !v.some((x) => x > 0)) return '';
  const w = 64;
  const h = 20;
  const mx = Math.max.apply(null, v.concat([1]));
  const pts = v
    .map((x, i) => ((i / (v.length - 1)) * w).toFixed(1) + ',' + (h - 1 - (x / mx) * (h - 2)).toFixed(1))
    .join(' ');
  return (
    `<svg class="rpt-spark" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}" aria-hidden="true">` +
    `<polyline points="${pts}" fill="none" stroke="${esc(color || 'var(--brand-700)')}" stroke-width="1.5" ` +
    'stroke-linejoin="round" stroke-linecap="round" opacity=".8"/></svg>'
  );
}

/**
 * Shared horizontal-bar list. `unit` suffixes each value ('%' for completeness).
 * Every label is esc()'d — county, facility and prescriber names are free text
 * that originated on a public consent form.
 * @param {Array<{l: string, v: number, c?: string, sub?: string}>} items
 * @param {{unit?: string, max?: number}} [opt]
 * @returns {string}
 */
export function hbars(items, opt) {
  const o = opt || {};
  const list = items || [];
  if (!list.length) return '<p class="rpt-empty">No data in the current view.</p>';
  const mx = Math.max.apply(null, [1].concat(list.map((i) => Number(i.v) || 0), o.max ? [o.max] : []));
  return (
    '<div class="rpt-hbars">' +
    list
      .map((i) => {
        const v = Number(i.v) || 0;
        const w = mx ? ((v / mx) * 100).toFixed(1) : '0';
        // Layout: label + its VALUE share a top line (the value is a direct label,
        // right-aligned, tabular), then a FULL-WIDTH proportional bar sits beneath.
        // Small counts stay legible — the number is read directly, the bar shows
        // magnitude relative to the biggest — instead of being squeezed into a
        // right-floated stub the way a fixed label column forced before.
        return (
          '<div class="rpt-hbar">' +
          '<div class="rpt-hbar-top">' +
          `<span class="rpt-hbar-l" title="${esc(i.l)}">${esc(i.l)}</span>` +
          `<b class="rpt-hbar-v">${esc(i.sub || fmtNum(v) + (o.unit || ''))}</b>` +
          '</div>' +
          `<span class="rpt-hbar-t"><span class="rpt-hbar-f" data-w="${w}" data-bg="${esc(i.c || 'var(--brand-700)')}"></span></span>` +
          '</div>'
        );
      })
      .join('') +
    '</div>'
  );
}

/**
 * A tone legend for an outcome-coloured bar list (closure reasons). Colour is a
 * SECONDARY encoding here — the reason label already carries identity — so this
 * is a visual key only (aria-hidden), and it shows ONLY the tones actually present
 * so it never advertises a colour that isn't on screen.
 * @param {Array<{c?: string}>} items
 * @returns {string}
 */
function toneLegendHTML(items) {
  const TONES = [
    { c: 'var(--ok-700)', l: 'positive' },
    { c: 'var(--brand-700)', l: 'neutral' },
    { c: 'var(--warn-700)', l: 'caution' },
    { c: 'var(--danger-700)', l: 'concerning' },
  ];
  const present = new Set((items || []).map((i) => i && i.c));
  const shown = TONES.filter((t) => present.has(t.c));
  if (shown.length < 2) return ''; // a legend for a single tone is just noise
  return (
    '<div class="rpt-tone-legend" aria-hidden="true">' +
    shown.map((t) => `<span class="rpt-tone"><span class="rpt-tone-dot" data-bg="${esc(t.c)}"></span>${esc(t.l)}</span>`).join('') +
    '</div>'
  );
}

/**
 * Legend rows beside a doughnut (label · dot · value).
 * @param {Array<{l: string, v: number, c?: string}>} items
 * @param {(v: number) => string} [fmt]
 * @returns {string}
 */
function legend(items, fmt) {
  const f = fmt || fmtNum;
  return (
    '<div class="rpt-legend">' +
    (items || [])
      .map(
        (i) =>
          '<div class="rpt-legend-row">' +
          `<span class="rpt-dot" data-bg="${esc(i.c || 'var(--brand-700)')}"></span>` +
          `<span class="rpt-legend-l">${esc(i.l)}</span>` +
          `<b class="rpt-num">${esc(f(Number(i.v) || 0))}</b>` +
          '</div>'
      )
      .join('') +
    '</div>'
  );
}

/**
 * A small two/three-column table. Cells are pre-escaped by the caller's mapper.
 * @param {string[]} head
 * @param {string[][]} rows raw (unescaped) cell strings
 * @param {{title?: string[], numeric?: number[]}} [opt] `numeric` = column indexes to right-align
 * @returns {string}
 */
function miniTable(head, rows, opt) {
  const o = opt || {};
  const num = o.numeric || [];
  if (!rows.length) return '<p class="rpt-empty">No data in the current view.</p>';
  return (
    '<div class="rpt-tw"><table class="rpt-mt">' +
    '<thead><tr>' +
    head.map((h, i) => `<th scope="col"${num.includes(i) ? ' class="n"' : ''}>${esc(h)}</th>`).join('') +
    '</tr></thead><tbody>' +
    rows
      .map(
        (r, ri) =>
          `<tr${o.title && o.title[ri] ? ` title="${esc(o.title[ri])}"` : ''}>` +
          r.map((c, i) => `<td${num.includes(i) ? ' class="n"' : ''}>${esc(c)}</td>`).join('') +
          '</tr>'
      )
      .join('') +
    '</tbody></table></div>'
  );
}

/** A single big stat with an optional gauge underneath. */
function bigStat(display, sub, pctForGauge, target) {
  let g = '';
  if (typeof pctForGauge === 'number' && isFinite(pctForGauge)) {
    const p = Math.max(0, Math.min(100, pctForGauge));
    const cls = p >= 80 ? 'ok' : p >= 50 ? 'warn' : 'danger';
    g =
      `<div class="rpt-gauge"><span class="rpt-gauge-f ${cls}" data-w="${p.toFixed(1)}"></span>` +
      (typeof target === 'number' ? `<span class="rpt-gauge-t" data-left="${Math.max(0, Math.min(100, target)).toFixed(1)}"></span>` : '') +
      '</div>';
  }
  return `<div class="rpt-bigstat">${esc(display)}</div>${g}${sub ? `<p class="rpt-note">${esc(sub)}</p>` : ''}`;
}

/** A short muted footnote under a tile body. */
function note(text) {
  return text ? `<p class="rpt-note">${esc(text)}</p>` : '';
}

// ─── Tile bodies (presentation only — every number arrives computed) ───────

// id → (result, ctx) => html. A metric with no entry here falls back to the
// generic value+sub body, so adding a registry metric can never crash the page.
/** @type {Record<string, (r: any, ctx: any) => string>} */
const TILE_BODY = {
  enrolment_by_month: (r) => canvas('enrolment_by_month', 230) + note(r.sub),
  consent_funnel: (r) => {
    const steps = (r.data && r.data.steps) || [];
    return (
      hbars(steps) +
      note(
        r.value === null
          ? 'No consent submissions in the current view.'
          : 'Conversion submitted → promoted: ' + fmtPct(r.value) + '. Patient-dimension slices do not narrow this card.'
      )
    );
  },
  pipeline_distribution: (r) => {
    const st = (r.data && r.data.stages) || [];
    return (
      '<div class="rpt-donut-row">' +
      canvas('pipeline_distribution', 190, 'rpt-donut') +
      legend(st.map((s) => ({ l: s.l, v: s.v, c: s.c }))) +
      '</div>' +
      note(r.sub)
    );
  },
  sla_board: (r) => {
    const rows = (r.data && r.data.rows) || [];
    return (
      miniTable(
        ['Clock', 'Breaches', 'State'],
        rows.map((x) => [x.l, x.n === null ? 'not measured' : fmtNum(x.n), x.n === null ? '—' : x.n === 0 ? 'On track' : x.n < 10 ? 'Watch' : 'Breached']),
        { numeric: [1], title: rows.map((x) => x.t) }
      ) + note('Hover a row for its exact definition.')
    );
  },
  programme_breakdown: (r) => {
    const rows = (r.data && r.data.rows) || [];
    if (!rows.length) return '<p class="rpt-empty">No programme data yet.</p>';
    return (
      '<div class="rpt-tw"><table class="rpt-mt"><thead><tr><th scope="col">Programme</th><th scope="col" class="n">Pending</th>' +
      '<th scope="col" class="n">Active</th><th scope="col" class="n">Closed</th><th scope="col" class="n">Archived</th><th scope="col" class="n">Serious AEs</th><th scope="col" class="n">AEs unsent</th></tr></thead><tbody>' +
      rows
        .map(
          (p) =>
            '<tr><th scope="row">' +
            progDotHTML(p.display_name, p.brand_accent_hex) +
            ' ' +
            esc(p.display_name) +
            `</th><td class="n">${esc(fmtNum(p.pending_consents))}</td>` +
            `<td class="n">${esc(fmtNum(p.active_cases))}</td>` +
            `<td class="n">${esc(fmtNum(p.closed_cases))}</td>` +
            `<td class="n">${esc(fmtNum(p.archived_cases))}</td>` +
            `<td class="n${Number(p.serious_aes_this_month) > 0 ? ' danger' : ''}">${esc(fmtNum(p.serious_aes_this_month))}</td>` +
            `<td class="n${Number(p.aes_pending_email) > 0 ? ' warn' : ''}">${esc(fmtNum(p.aes_pending_email))}</td></tr>`
        )
        .join('') +
      '</tbody></table></div>' +
      note('Programme-wide current state — not narrowed by the period or slice chips.')
    );
  },
  sha_coverage_gap: (r) => {
    const d = r.data || {};
    return (
      hbars(d.items || []) +
      note(
        r.value === null
          ? ''
          : fmtNum(r.value) + ' of ' + fmtNum(d.activeTotal || 0) + ' active patients are not covered by SHA — the programme absorbs their cost.'
      )
    );
  },
  sha_tat_trend: (r) => canvas('sha_tat_trend', 200) + note(r.sub),
  sha_pending_ageing: (r) => {
    const d = r.data || {};
    return (
      hbars(d.items || []) +
      note(r.sub + (d.over15 ? ' · ' + fmtNum(d.over15) + ' waiting 15+ days' : ''))
    );
  },
  sha_approvals_by_month: (r) => canvas('sha_approvals_by_month', 200) + note(r.sub),
  time_to_treatment_trend: (r) => canvas('time_to_treatment_trend', 200) + note(r.sub),
  refill_ontime_trend: (r) => canvas('refill_ontime_trend', 200) + note(r.sub),
  persistence_by_cycle: (r) => canvas('persistence_by_cycle', 200) + note(r.sub),
  cohort_retention: (r) => canvas('cohort_retention', 210) + note(r.sub + ' · exit date approximated from the last row update'),
  closure_reasons: (r) => {
    const items = (r.data && r.data.items) || [];
    return hbars(items) + toneLegendHTML(items) + note(r.sub);
  },
  at_risk_patients: (r) => {
    const list = (r.data && r.data.list) || [];
    if (!list.length) return '<p class="rpt-empty">No high or critical risk patients in the current view.</p>';
    return (
      '<div class="rpt-tw"><table class="rpt-mt"><thead><tr><th scope="col">Patient</th><th scope="col">County</th><th scope="col">Risk</th><th scope="col">Owner</th></tr></thead><tbody>' +
      list
        .slice(0, 8)
        .map(
          (c) =>
            // A real <button> in the first cell, not role="button" on the <tr>: the
            // latter overrode the row role, orphaned every <td> and made the row's
            // accessible name the run-on of patient + county + risk + owner (A-05).
            // The row itself carries no click/hover affordance (item 10, second-
            // opinion pass): County, Risk and Owner are inert <td>s, and giving the
            // whole row a pointer cursor and hover highlight advertised a click
            // target three-quarters of which was a no-op. The button is the only
            // thing Tab reaches too, so scoping the affordance to it does not make
            // pointer and keyboard users see different clickable areas.
            '<tr>' +
            `<td><button type="button" class="linklike rpt-rowbtn" data-action="rpt-open-case" data-case="${esc(c.case_id)}" ` +
            `aria-label="${esc((c.patient_name || '—') + ' — open patient')}">${esc(c.patient_name || '—')}</button></td><td>${esc(c.county || '—')}</td>` +
            `<td><span class="rpt-tag ${c.risk_bucket === 'critical' ? 'danger' : 'warn'}">${esc(c.risk_bucket)}</span></td>` +
            `<td class="${c.assigned_to ? '' : 'danger'}">${esc((c.owner && c.owner.full_name) || 'Unassigned')}</td></tr>`
        )
        .join('') +
      '</tbody></table></div>' +
      note(fmtNum(list.length) + ' flagged · showing ' + Math.min(8, list.length) + ' · staff only — excluded from every export.')
    );
  },
  retention_by_month: (r) => {
    const rows = (r.data && r.data.rows) || [];
    return miniTable(
      ['Month', 'Enrolled', 'Still active', 'Retention'],
      rows.map((x) => [x.l, fmtNum(x.init), fmtNum(x.act), fmtPct(x.pct)]),
      { numeric: [1, 2, 3] }
    );
  },
  ae_trend: (r) => canvas('ae_trend', 200) + note(r.sub),
  ae_rate_per_100: (r) => canvas('ae_rate_per_100', 190) + note(r.sub),
  comms_channel_mix: (r) => {
    const items = (r.data && r.data.items) || [];
    return '<div class="rpt-donut-row">' + canvas('comms_channel_mix', 180, 'rpt-donut') + legend(items) + '</div>' + note(r.sub);
  },
  no_contact_permission: (r) => bigStat(r.display, r.sub),
  cost_breakdown: (r) => {
    const items = (r.data && r.data.items) || [];
    return '<div class="rpt-donut-row">' + canvas('cost_breakdown', 190, 'rpt-donut') + legend(items, fmtMoney) + '</div>' + note(r.sub);
  },
  cost_per_patient_trend: (r) => canvas('cost_per_patient_trend', 200) + note(r.sub),
  cost_by_programme: (r) => hbars((r.data && r.data.items) || []) + note(r.sub),
  patients_by_county: (r) => canvas('patients_by_county', 300) + note(r.sub),
  programme_county_heat: (r) => {
    const d = r.data || {};
    const cols = d.cols || [];
    const grid = d.grid || [];
    if (!cols.length || !grid.length) return '<p class="rpt-empty">No county data in the current view.</p>';
    const mx = d.max || 1;
    return (
      '<div class="rpt-tw"><table class="rpt-mt rpt-heat"><thead><tr><th scope="col"><span class="visually-hidden">County</span></th>' +
      cols.map((c) => `<th scope="col" class="n">${esc(c)}</th>`).join('') +
      '</tr></thead><tbody>' +
      grid
        .map(
          (row) =>
            `<tr><th scope="row">${esc(row.l)}</th>` +
            row.cells
              .map((v) => {
                const a = v ? (0.12 + 0.7 * (v / mx)).toFixed(2) : '0';
                return `<td class="n" data-heat="${a}"${v / mx > 0.55 ? ' data-heat-ink="1"' : ''}>${v ? esc(fmtNum(v)) : ''}</td>`;
              })
              .join('') +
            '</tr>'
        )
        .join('') +
      '</tbody></table></div>' +
      note('Empty cells are genuine access gaps, not missing data.')
    );
  },
  urban_rural: (r) => {
    const d = r.data || {};
    return (
      hbars(d.items || []) +
      note(
        (r.value === null ? 'No county data yet' : fmtPct(r.value) + ' of located patients are rural') +
          (d.unknown ? ' · ' + fmtNum(d.unknown) + ' without a county' : '')
      )
    );
  },
  patients_by_facility: (r) => hbars((r.data && r.data.items) || []) + note(r.sub),
  top_prescribers: (r) => hbars((r.data && r.data.items) || []) + note(r.sub),
  nurse_caseload: (r) => hbars((r.data && r.data.items) || []) + note(r.sub),
  caseload_balance: (r) => {
    // The gauge reads "how even is the team": 1.0× fills it, 2.0× empties it.
    const g = r.value === null ? undefined : Math.max(0, Math.min(100, (2 - Number(r.value)) * 100));
    return bigStat(r.display, r.sub, g);
  },
  consents_processed: (r) => canvas('consents_processed', 190) + note(r.sub),
  field_completeness: (r) => hbars((r.data && r.data.items) || [], { unit: '%', max: 100 }) + note(r.sub),
  hygiene_flags: (r) => {
    const rows = (r.data && r.data.rows) || [];
    return miniTable(['Issue', 'Count'], rows.map((x) => [x.l, fmtNum(x.v)]), { numeric: [1], title: rows.map((x) => x.t) });
  },

  // ── Clinical profile (growth) ────────────────────────────────────────────
  diagnosis_mix: (r) => {
    const d = r.data || {};
    return hbars(d.items || []) + note(d.missing ? fmtNum(d.missing) + ' patients have no diagnosis recorded.' : r.sub);
  },
  cancer_stage_mix: (r) => {
    const d = r.data || {};
    return (
      hbars(d.items || []) +
      note(d.missing ? fmtNum(d.missing) + ' patients were enrolled without a stage — read every outcome number with that in mind.' : r.sub)
    );
  },

  // ── Treatment journey (adherence) ────────────────────────────────────────
  adherence_bands: (r) => {
    const d = r.data || {};
    return (
      hbars(d.items || []) +
      toneLegendHTML(d.items || []) +
      note(
        r.value === null
          ? 'Needs two dated dispenses with a days-supply recorded.'
          : 'Median possession ratio ' + fmtPct(d.median) + ' across ' + fmtNum(d.scored) + ' scored patients. Anyone with one dispense is not scored.'
      )
    );
  },
  treatment_start_confirmed: (r) => {
    const g = r.value === null ? undefined : Number(r.value);
    return bigStat(r.display, r.sub, g, 95) + hbars((r.data && r.data.items) || []);
  },
  therapy_stops: (r) => hbars((r.data && r.data.items) || []) + note(r.sub),
  medication_switches: (r) => hbars((r.data && r.data.items) || []) + note(r.sub),

  // ── Retention ────────────────────────────────────────────────────────────
  delivery_method_mix: (r) => {
    const d = r.data || {};
    return hbars(d.items || []) + note(d.missing ? fmtNum(d.missing) + ' active patients have no delivery method recorded.' : r.sub);
  },

  // ── Safety · pharmacovigilance ───────────────────────────────────────────
  ae_reporting_timeliness: (r) => {
    const d = r.data || {};
    const g = r.value === null ? undefined : Number(r.value);
    return (
      bigStat(r.display, r.sub, g, 100) +
      note(
        r.value === null
          ? 'No adverse event has been dispatched to pharmacovigilance yet.'
          : 'Measured on events already dispatched. The ' + fmtNum(d.pending || 0) + ' still waiting are on the backlog tile, so they cannot flatter this figure.'
      )
    );
  },
  pv_dispatch_backlog: (r) => {
    const d = r.data || {};
    if (r.value === 0) return bigStat('0', 'nothing waiting to be dispatched') + note('Checked and clear — every recorded event has a dispatch stamp.');
    return (
      hbars(d.items || []) +
      toneLegendHTML(d.items || []) +
      note(
        (d.serious48 ? fmtNum(d.serious48) + ' serious event(s) past the 48-hour line. ' : 'No serious event is past 48 hours. ') +
          'Median wait ' +
          fmtDays(d.medianAge)
      )
    );
  },
  ae_severity_mix: (r) => {
    const d = r.data || {};
    return (
      hbars(d.items || []) +
      toneLegendHTML(d.items || []) +
      note(d.missing ? fmtNum(d.missing) + ' events carry no severity grade.' : 'Every event in view is graded.')
    );
  },
  ae_outcome_mix: (r) => {
    const d = r.data || {};
    return (
      hbars(d.items || []) +
      toneLegendHTML(d.items || []) +
      note(d.missing ? fmtNum(d.missing) + ' events have no outcome recorded — usually follow-up that stopped, not patients who are fine.' : r.sub)
    );
  },
  ae_onset_lag: (r) => {
    const d = r.data || {};
    return (
      bigStat(r.display, r.sub) +
      note(
        r.value === null
          ? 'No event carries a usable onset date.'
          : 'This is the clock that decides whether the 24-hour window was winnable at all.' + (d.missing ? ' ' + fmtNum(d.missing) + ' without an onset date.' : '')
      )
    );
  },
  toxicity_by_cycle: (r) => canvas('toxicity_by_cycle', 210) + note(r.sub),

  // ── Financing ────────────────────────────────────────────────────────────
  billable_split: (r) => {
    const items = ((r.data && r.data.items) || []).map((i) => ({ l: i.l, v: i.v, c: i.c, sub: fmtMoney(i.v) }));
    return hbars(items) + toneLegendHTML(items) + note(r.sub);
  },
  spend_run_rate: (r) => {
    const d = r.data || {};
    return (
      hbars(d.items || []) +
      note(
        r.value === null
          ? 'No spend recorded in the last three complete months.'
          : 'Last three COMPLETE months. The current part-month is excluded on purpose — it always reads as a collapse.'
      )
    );
  },

  // ── Pipeline ageing + reachability ───────────────────────────────────────
  stage_ageing: (r) => {
    const d = r.data || {};
    const items = (d.items || []).map((i) => ({ l: i.l + ' (' + fmtNum(i.n) + ')', v: i.v, c: i.c, sub: fmtDays(i.v) }));
    return (
      hbars(items) +
      toneLegendHTML(items) +
      note(
        r.value === null
          ? 'No active patient has a recorded stage move yet.'
          : 'Median days sat in each stage. ' +
            (d.stuck ? fmtNum(d.stuck) + ' patient(s) past 30 days. ' : 'Nobody is past 30 days. ') +
            (d.untracked ? fmtNum(d.untracked) + ' have never moved stage.' : '')
      )
    );
  },
  reach_rate_by_channel: (r) => {
    const d = r.data || {};
    return (
      hbars(d.items || [], { unit: '%', max: 100 }) +
      note(
        r.value === null
          ? 'No contact attempts logged in this view.'
          : 'Counts attempts that were LOGGED — a channel used but not written down looks unused, not unsuccessful.'
      )
    );
  },
  preference_honoured: (r) => {
    const d = r.data || {};
    const g = r.value === null ? undefined : Number(r.value);
    return (
      bigStat(r.display, r.sub, g, 100) +
      hbars(d.items || []) +
      note(
        r.value === null
          ? 'No attempt yet on a patient with a recorded contact preference.'
          : (d.offChannel && d.offChannel.length
              ? 'Off-consent attempts were on: ' + d.offChannel.map((i) => i.l + ' (' + fmtNum(i.v) + ')').join(', ') + '. '
              : 'Every logged attempt used a channel the patient consented to. ') +
            'Each point below 100% is a data-protection exception, not a service one.'
      )
    );
  },

  // ── Equity ───────────────────────────────────────────────────────────────
  age_mix: (r) => canvas('age_mix', 200) + note(r.sub),
  sex_mix: (r) => {
    const d = r.data || {};
    return hbars(d.items || []) + toneLegendHTML(d.items || []) + note(d.missing ? fmtNum(d.missing) + ' patients have no sex recorded.' : r.sub);
  },
};

// Tiles that should span more than one grid column, and the per-section grid.
/** @type {Record<string, number>} */
const TILE_SPAN = {
  enrolment_by_month: 2,
  patients_by_county: 2,
  programme_breakdown: 2,
  programme_county_heat: 2,
  ae_trend: 2,
  retention_by_month: 2,
  cohort_retention: 2,
};

/**
 * A chart wrapper + canvas. `label` names the chart for a screen reader and
 * `summary` (a one-line peak/direction sentence) is the text alternative — a
 * canvas-only tile otherwise locks its entire insight in pixels, and a page of
 * canvases all announcing "chart" tells a non-sighted reader nothing at all.
 * Height rides on data-h, not an inline style: the deployed CSP is
 * `style-src 'self'` with no 'unsafe-inline', which blocks style ATTRIBUTES too,
 * and .rpt-canvas-wrap carries no fallback height — so an inline style meant
 * every chart was 0px tall in production and captureCharts silently skipped them
 * all, exporting a deck with zero chart slides that still toasted success.
 * @param {string} id @param {number} h @param {string} [cls]
 * @returns {string}
 */
function canvas(id, h, cls) {
  // The label comes from the registry, so it cannot drift from the tile heading.
  const label = (getMetric(id) || {}).label || 'chart';
  return (
    `<div class="rpt-canvas-wrap${cls ? ' ' + cls : ''}" data-h="${Number(h) || 0}">` +
    `<canvas id="rptc-${esc(id)}" role="img" aria-label="${esc(label)}" aria-describedby="rptc-sum-${esc(id)}"></canvas>` +
    `</div><p class="rpt-sr" id="rptc-sum-${esc(id)}"></p>`
  );
}

// ─── Chart painters (VENDORED Chart.js, destroy-before-reuse) ─────────────

const CHART_COLORS = {
  navy: '#1F4477',
  navy3: '#2B5BA8',
  cyan: '#0891B2',
  lime: '#65A30D',
  amber: '#D97706',
  ok: '#16773B',
  danger: '#B91C1C',
  violet: '#7C3AED',
  grey: '#94A3B8',
};

/**
 * Create a chart on a canvas, destroying whatever instance held it before.
 * A no-op when Chart.js failed to load or the canvas is absent (a filter change
 * that hides a tile must not throw).
 * @param {string} id canvas id WITHOUT the 'rptc-' prefix
 * @param {any} config Chart.js config
 * @returns {void}
 */
function paint(id, config) {
  const Chart = getChart();
  const d = doc();
  if (!Chart || !d) return;
  const key = 'rptc-' + id;
  const el = d.getElementById(key);
  const prev = _charts.get(key);
  if (prev && typeof prev.destroy === 'function') prev.destroy();
  _charts.delete(key);
  if (!el) return;
  try {
    _charts.set(key, new Chart(el, config));
  } catch (e) {
    // A chart that cannot draw must not take the page down — the tile keeps its
    // caption and footnote, which still carry the number.
    console.warn('chart paint failed:', id, e);
  }
}

/** Destroy every live chart (view teardown / before a full re-render). @returns {void} */
export function destroyCharts() {
  for (const [, c] of _charts) if (c && typeof c.destroy === 'function') c.destroy();
  _charts.clear();
}

const BASE_OPTS = { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } } };

/** @param {Array<{l: string, v: number}>} s @returns {string[]} */
const labelsOf = (s) => (s || []).map((p) => p.l);
/** @param {Array<{l: string, v: number}>} s @returns {number[]} */
const valuesOf = (s) => (s || []).map((p) => Number(p.v) || 0);

/**
 * Paint every chart tile for the current results. Called after the DOM is written.
 * @param {Record<string, any>} agg
 * @returns {void}
 */
function paintCharts(agg) {
  const g = (id) => (agg[id] && agg[id].data) || {};

  paint('enrolment_by_month', {
    type: 'bar',
    data: {
      labels: labelsOf(g('enrolment_by_month').series),
      datasets: [{ data: valuesOf(g('enrolment_by_month').series), backgroundColor: CHART_COLORS.navy, borderRadius: 4, barPercentage: 0.6 }],
    },
    options: Object.assign({}, BASE_OPTS, {
      scales: { y: { beginAtZero: true }, x: { ticks: { maxRotation: 45, font: { size: 10 } } } },
    }),
  });

  const stages = g('pipeline_distribution').stages || [];
  paint('pipeline_distribution', {
    type: 'doughnut',
    data: { labels: stages.map((s) => s.s), datasets: [{ data: stages.map((s) => s.v), backgroundColor: stages.map((s) => s.c), borderWidth: 0 }] },
    options: Object.assign({}, BASE_OPTS, { cutout: '58%' }),
  });

  paint('sha_tat_trend', lineCfg(g('sha_tat_trend').series, CHART_COLORS.navy, getMetric('sha_tat_trend').target));
  paint('sha_approvals_by_month', lineCfg(g('sha_approvals_by_month').series, CHART_COLORS.lime));
  paint('refill_ontime_trend', lineCfg(g('refill_ontime_trend').series, CHART_COLORS.ok, getMetric('refill_ontime_trend').target, 100));
  paint('ae_rate_per_100', lineCfg(g('ae_rate_per_100').series, CHART_COLORS.danger));
  paint('cost_per_patient_trend', lineCfg(g('cost_per_patient_trend').series, CHART_COLORS.navy));

  paint('time_to_treatment_trend', {
    type: 'bar',
    data: {
      labels: labelsOf(g('time_to_treatment_trend').series),
      datasets: [{ data: valuesOf(g('time_to_treatment_trend').series), backgroundColor: CHART_COLORS.cyan, borderRadius: 4, barPercentage: 0.6 }],
    },
    options: Object.assign({}, BASE_OPTS, { scales: { y: { beginAtZero: true } } }),
  });

  paint('persistence_by_cycle', {
    type: 'bar',
    data: {
      labels: labelsOf(g('persistence_by_cycle').series),
      datasets: [{ data: valuesOf(g('persistence_by_cycle').series), backgroundColor: CHART_COLORS.navy3, borderRadius: 4, barPercentage: 0.65 }],
    },
    options: Object.assign({}, BASE_OPTS, { scales: { y: { beginAtZero: true } } }),
  });

  paint('consents_processed', {
    type: 'bar',
    data: {
      labels: labelsOf(g('consents_processed').series),
      datasets: [{ data: valuesOf(g('consents_processed').series), backgroundColor: CHART_COLORS.cyan, borderRadius: 4, barPercentage: 0.6 }],
    },
    options: Object.assign({}, BASE_OPTS, { scales: { y: { beginAtZero: true } } }),
  });

  paint('age_mix', {
    type: 'bar',
    data: {
      labels: labelsOf(g('age_mix').series),
      datasets: [{ data: valuesOf(g('age_mix').series), backgroundColor: CHART_COLORS.navy3, borderRadius: 4, barPercentage: 0.65 }],
    },
    options: Object.assign({}, BASE_OPTS, { scales: { y: { beginAtZero: true } } }),
  });

  // Events per cycle beside the patients who actually reached that cycle — the
  // count alone cannot be read as a rate, so both bars are always drawn together.
  const tox = g('toxicity_by_cycle').series || [];
  paint('toxicity_by_cycle', {
    type: 'bar',
    data: {
      labels: labelsOf(tox),
      datasets: [
        { label: 'Adverse events', data: tox.map((p) => p.v), backgroundColor: CHART_COLORS.danger, borderRadius: 4, barPercentage: 0.7 },
        { label: 'Patients reaching cycle', data: tox.map((p) => p.patients), backgroundColor: CHART_COLORS.navy3, borderRadius: 4, barPercentage: 0.7 },
      ],
    },
    options: Object.assign({}, BASE_OPTS, { scales: { y: { beginAtZero: true } } }),
  });

  const ae = g('ae_trend').series || [];
  paint('ae_trend', {
    type: 'bar',
    data: {
      labels: labelsOf(ae),
      datasets: [
        { label: 'All adverse events', data: ae.map((p) => p.v), backgroundColor: CHART_COLORS.cyan, borderRadius: 4, barPercentage: 0.7 },
        { label: 'of which serious', data: ae.map((p) => p.serious), backgroundColor: CHART_COLORS.danger, borderRadius: 4, barPercentage: 0.7 },
      ],
    },
    options: Object.assign({}, BASE_OPTS, {
      plugins: { legend: { display: true, position: 'bottom', labels: { font: { size: 10 }, boxWidth: 12 } } },
      scales: { y: { beginAtZero: true } },
    }),
  });

  const coh = g('cohort_retention');
  const palette = [CHART_COLORS.navy, CHART_COLORS.cyan, CHART_COLORS.lime, CHART_COLORS.amber, CHART_COLORS.violet, CHART_COLORS.danger];
  paint('cohort_retention', {
    type: 'line',
    data: {
      labels: (coh.marks || []).map((m) => m + ' mo'),
      datasets: (coh.series || []).map((s, i) => ({
        label: s.l,
        data: (s.points || []).map((p) => p.v),
        borderColor: palette[i % palette.length],
        backgroundColor: 'transparent',
        tension: 0.3,
        pointRadius: 3,
        spanGaps: true,
      })),
    },
    options: Object.assign({}, BASE_OPTS, {
      plugins: { legend: { display: true, position: 'bottom', labels: { font: { size: 10 }, boxWidth: 12 } } },
      scales: { y: { beginAtZero: true, max: 100, ticks: { callback: (v) => v + '%' } } },
    }),
  });

  const cm = g('comms_channel_mix').items || [];
  paint('comms_channel_mix', {
    type: 'doughnut',
    data: { labels: cm.map((i) => i.l), datasets: [{ data: cm.map((i) => i.v), backgroundColor: cm.map((i) => i.c), borderWidth: 0 }] },
    options: Object.assign({}, BASE_OPTS, { cutout: '58%' }),
  });

  const cb = g('cost_breakdown').items || [];
  paint('cost_breakdown', {
    type: 'doughnut',
    data: { labels: cb.map((i) => i.l), datasets: [{ data: cb.map((i) => i.v), backgroundColor: cb.map((i) => i.c), borderWidth: 0 }] },
    options: Object.assign({}, BASE_OPTS, { cutout: '58%' }),
  });

  const cty = g('patients_by_county').items || [];
  paint('patients_by_county', {
    type: 'bar',
    data: { labels: cty.map((i) => i.l), datasets: [{ data: cty.map((i) => i.v), backgroundColor: CHART_COLORS.navy, borderRadius: 4, barPercentage: 0.7 }] },
    options: Object.assign({}, BASE_OPTS, { indexAxis: 'y', scales: { x: { beginAtZero: true } } }),
  });
}

/**
 * A filled line chart with an optional dashed target line.
 * @param {Array<{l: string, v: number}>} series @param {string} color @param {number} [target] @param {number} [max]
 */
function lineCfg(series, color, target, max) {
  /** @type {any[]} */
  const datasets = [
    {
      data: valuesOf(series),
      borderColor: color,
      backgroundColor: 'transparent',
      fill: false,
      tension: 0.3,
      pointRadius: 3,
      pointBackgroundColor: color,
    },
  ];
  if (typeof target === 'number' && (series || []).length) {
    datasets.push({
      label: 'target',
      data: (series || []).map(() => target),
      borderColor: CHART_COLORS.danger,
      borderDash: [4, 3],
      borderWidth: 1.5,
      pointRadius: 0,
      fill: false,
      tension: 0,
    });
  }
  return {
    type: 'line',
    data: { labels: labelsOf(series), datasets },
    options: Object.assign({}, BASE_OPTS, {
      scales: { y: Object.assign({ beginAtZero: true }, typeof max === 'number' ? { max } : {}), x: { ticks: { maxRotation: 45, font: { size: 10 } } } },
    }),
  };
}

// ─── The rule-based executive narrative (NO AI — R-002) ───────────────────

/**
 * Build the exec-summary clauses from computed metric results. Every clause
 * GUARDS ITS OWN INPUTS: a metric that could not be computed contributes no
 * sentence at all, rather than a sentence containing '—'. This is a template
 * with computed tokens; there is no model, no generation, and no clause that
 * cannot be traced to a registry metric. Pure — unit-tested.
 *
 * @param {Record<string, any>} agg current-period results
 * @param {Record<string, any>|null} prev previous-period results (Compare on) or null
 * @param {{periodLabel: string, filtered: boolean, topProgramme?: string|null, topCounties?: string[]}} ctx
 * @returns {string[]} plain-text clauses, in order
 */
export function execClauses(agg, prev, ctx) {
  const a = agg || {};
  const p = prev || null;
  const out = [];
  const val = (id) => (a[id] && a[id].value !== undefined ? a[id].value : null);
  const pval = (id) => (p && p[id] && p[id].value !== undefined ? p[id].value : null);

  const enrolled = val('enrolled');
  const active = val('active');
  const pEnrolled = pval('enrolled');
  if (enrolled !== null && pEnrolled) {
    const d = Math.abs(Math.round(((enrolled - pEnrolled) / pEnrolled) * 100));
    out.push(`Enrolment ${enrolled >= pEnrolled ? 'grew' : 'fell'} ${d}% against the previous period.`);
  } else if (enrolled !== null) {
    out.push(`${fmtNum(enrolled)} patient${enrolled === 1 ? '' : 's'} in the current view, ${fmtNum(active === null ? 0 : active)} active.`);
  }

  if (ctx && ctx.topProgramme) out.push(`Largest programme: ${ctx.topProgramme}.`);
  if (ctx && ctx.topCounties && ctx.topCounties.length) out.push(`Strongest counties: ${ctx.topCounties.join(', ')}.`);

  const tat = a.sha_tat_trend && a.sha_tat_trend.data;
  if (tat && typeof tat.latest === 'number' && typeof tat.prev === 'number') {
    out.push(
      `SHA approval turnaround is ${tat.latest > tat.prev ? 'worsening' : 'improving'} (${Math.round(tat.latest)} days, against ${Math.round(tat.prev)} the month before).`
    );
  } else if (tat && typeof tat.latest === 'number') {
    out.push(`SHA approval turnaround: ${Math.round(tat.latest)} days median.`);
  }

  const onTime = val('refills_on_time');
  if (onTime !== null) {
    const t = getMetric('refills_on_time').target;
    out.push(`Refill punctuality (proxy) sits at ${onTime}%${typeof t === 'number' ? `, against a ${t}% target` : ''}.`);
  }

  const gap = val('sha_coverage_gap');
  if (gap) out.push(`${fmtNum(gap)} active patient${gap === 1 ? ' is' : 's are'} not covered by SHA — the programme absorbs their cost.`);

  const unassigned = a.nurse_caseload && a.nurse_caseload.data && a.nurse_caseload.data.unassigned;
  if (unassigned) out.push(`${fmtNum(unassigned)} active patient${unassigned === 1 ? ' is' : 's are'} unassigned.`);

  const ae = val('serious_ae_mtd');
  if (ae) out.push(`${fmtNum(ae)} serious adverse event${ae === 1 ? '' : 's'} reported this month.`);

  const ltfu = val('ltfu');
  if (ltfu) out.push(`Loss to follow-up: ${ltfu}%.`);

  const cost = val('cost_per_patient');
  if (cost !== null) out.push(`Pass-through cost per active patient: ${fmtMoney(cost)}.`);

  return out;
}

// ─── Filter bar ────────────────────────────────────────────────────────────

/** @param {string} label @param {string} value @param {string} cur @param {string} action @returns {string} */
function segBtn(label, value, cur, action, dataKey) {
  const on = value === cur;
  return (
    `<button type="button" class="rpt-seg-b${on ? ' on' : ''}" data-action="${esc(action)}" data-${esc(dataKey)}="${esc(value)}" ` +
    `aria-pressed="${on ? 'true' : 'false'}">${esc(label)}</button>`
  );
}

// The filter ribbon, redesigned (Power BI filter-pane pattern) into ONE slim row:
// Period dropdown-button · Filters button (with an active-dim count badge) ·
// Compare toggle · applied-filter chips (only when set) · Clear all · read-only
// Programme hint. The 8 slice dimensions + the Clock live in an on-demand panel
// (filterPanelHTML) that reuses the EXISTING sliceMenuHTML listbox verbatim. This
// is a presentation reshape only — _filters, SLICE_DIMS, PERIODS, CLOCKS,
// sliceValueCounts and every filter action are unchanged.
function filterBarHTML() {
  const f = _filters;
  const prog = store.get(SLICES.PROGRAMMES) || {};
  const activeProg = (store.get(SLICES.FILTERS) || {}).programme || 'all';
  const progLabel = pillLabel(activeProg, prog.byCode || {});

  const activeDims = SLICE_DIMS.filter((d) => (f.slice[d.k] || []).length).length;
  const anyFilter = f.period !== 'all' || f.compare || activeDims > 0;

  // Just the slim bar. The pivot follows it inside the sticky .rpt-chrome wrapper
  // (see render), and the filter PANEL is emitted by render OUTSIDE that wrapper —
  // .rpt-chrome is position:sticky (a stacking context) and would trap the panel's
  // position:fixed below the app top bar, so the panel must live in #rpt-root
  // directly, where it paints in the root context and overlays everything.
  return (
    '<div class="rpt-filterbar" role="region" aria-label="Report filters">' +
    '<div class="rpt-fb">' +
    periodButtonHTML() +
    filtersButtonHTML(activeDims) +
    compareChipHTML() +
    appliedChipsHTML() +
    '<span class="rpt-fb-sp"></span>' +
    (anyFilter ? '<button type="button" class="rpt-clear" data-action="rpt-clear">Clear all</button>' : '') +
    progHintHTML(progLabel) +
    '</div>' +
    '</div>'
  );
}

/** The Period dropdown-button: current period label + muted clock suffix + chevron; the popover hangs off it. */
function periodButtonHTML() {
  const f = _filters;
  const pLbl = (PERIODS.find((p) => p.k === f.period) || {}).l || 'All time';
  const clockLbl = (CLOCKS.find((c) => c.k === f.clock) || {}).l || 'Enrolment';
  // Show the literal range ONLY when it resolves to a real window. A reversed or
  // incomplete custom range resolves to null (periodRange → All time), so echoing
  // the raw dates would advertise a filter the report is NOT applying — fall back
  // to the plain 'Custom…' so the label never disagrees with the numbers.
  const validCustom = f.period === 'custom' && periodRange('custom', new Date(), f.custom || {});
  const label = validCustom ? esc(f.custom.from + ' → ' + f.custom.to) : esc(pLbl);
  return (
    '<span class="rpt-drop">' +
    `<button type="button" id="rpt-period-btn" class="rpt-period-btn" data-action="rpt-period-open" ` +
    `aria-haspopup="true" aria-expanded="${_periodOpen ? 'true' : 'false'}">` +
    `<span class="rpt-period-l">${label}</span>` +
    `<span class="rpt-period-clock">· ${esc(String(clockLbl).toLowerCase())}</span>` +
    ic(IC_CHEVRON, 14) +
    '</button>' +
    (_periodOpen ? periodPopoverHTML() : '') +
    '</span>'
  );
}

/** The period popover: a radiogroup of the 6 periods + (for Custom) date inputs and an explicit Apply. */
function periodPopoverHTML() {
  const f = _filters;
  const radios = PERIODS.map((p) => {
    const on = f.period === p.k;
    return (
      `<button type="button" class="rpt-period-opt${on ? ' on' : ''}" role="radio" aria-checked="${on ? 'true' : 'false'}" ` +
      `tabindex="${on ? '0' : '-1'}" data-action="rpt-period" data-period="${esc(p.k)}">${esc(p.l)}</button>`
    );
  }).join('');
  const custom =
    f.period === 'custom'
      ? '<div class="rpt-period-custom">' +
        `<label class="rpt-cl" for="rpt-from">From</label><input type="date" id="rpt-from" class="rpt-date" value="${esc((f.custom && f.custom.from) || '')}"/>` +
        `<label class="rpt-cl" for="rpt-to">To</label><input type="date" id="rpt-to" class="rpt-date" value="${esc((f.custom && f.custom.to) || '')}"/>` +
        `<button type="button" class="btn sm pri rpt-period-apply" data-action="rpt-period-custom-apply">Apply</button>` +
        '</div>'
      : '';
  return (
    '<div class="rpt-period-pop" id="rpt-period-pop">' +
    `<div class="rpt-pop-g" role="radiogroup" aria-label="Reporting period">${radios}</div>` +
    custom +
    '</div>'
  );
}

/** The Filters button + active-dim count badge (badge hidden at 0). Opens the panel. */
function filtersButtonHTML(count) {
  return (
    `<button type="button" id="rpt-filters-btn" class="rpt-filters-btn${_panelOpen ? ' on' : ''}" data-action="rpt-filters-open" ` +
    `aria-haspopup="dialog" aria-expanded="${_panelOpen ? 'true' : 'false'}" aria-controls="rpt-panel-filters" ` +
    `aria-label="Filters${count ? ', ' + count + ' active' : ''}">` +
    ic(IC_FILTER, 15) +
    '<span>Filters</span>' +
    (count ? `<span class="rpt-filters-badge" aria-hidden="true">${esc(fmtNum(count))}</span>` : '') +
    '</button>'
  );
}

/** The Compare toggle chip (always visible; filled navy when on). Guard + handler unchanged. */
function compareChipHTML() {
  const on = _filters.compare;
  return (
    `<button type="button" id="rpt-compare-btn" class="rpt-chip rpt-compare-chip${on ? ' on' : ''}" data-action="rpt-compare" ` +
    `aria-pressed="${on ? 'true' : 'false'}">` +
    ic(IC_COMPARE, 15) +
    '<span class="rpt-compare-t">Compare</span>' +
    '</button>'
  );
}

/**
 * The applied-filter chips — rendered ONLY when something is set. One removable
 * chip per active slice dim (body deep-links into that group in the panel; the ✕
 * clears the whole dim), plus a compare chip when compare is on. Per-VALUE removal
 * still lives in the panel listbox. Built off the pure activeFilterSummary().
 */
function appliedChipsHTML() {
  const s = activeFilterSummary(_filters);
  if (!s.dims.length) return '';
  // One chip per active slice DIM. Compare is deliberately NOT chipped here — it
  // already has its own always-visible toggle in the slim row (filled when on),
  // so a second "Compare: previous ✕" affordance would just be a duplicate
  // control. The chip's accessible name carries the SELECTED VALUE, not only the
  // dimension, so a screen-reader user hears what a sighted user sees.
  const chips = s.dims.map((c) => {
    const more = c.extra ? ', +' + fmtNum(c.extra) + ' more' : '';
    return (
      '<span class="rpt-applied-chip">' +
      `<button type="button" class="rpt-applied-b" data-action="rpt-menu" data-dim="${esc(c.dim)}" ` +
      `aria-label="Edit ${esc(c.label)} filter: ${esc(c.first)}${esc(more)}">` +
      `${esc(c.label)}: ${esc(c.first)}${c.extra ? ` <span class="rpt-applied-x">+${esc(fmtNum(c.extra))}</span>` : ''}</button>` +
      `<button type="button" class="rpt-chip-x" data-action="rpt-chip-remove" data-dim="${esc(c.dim)}" aria-label="Remove ${esc(c.label)} filter">${ic(IC_X, 12)}</button>` +
      '</span>'
    );
  });
  return '<span class="rpt-applied">' + chips.join('') + '</span>';
}

/**
 * The read-only Programme hint — the programme filter is GLOBAL (set by the pill
 * in the app top bar), shown here as a non-editable reminder. Deliberately not a
 * control: two places to set one filter is how they end up disagreeing.
 */
function progHintHTML(progLabel) {
  return (
    '<span class="rpt-prog-hint" aria-disabled="true" title="Set by the programme pill in the top bar">' +
    ic(IC_LOCK, 12) +
    `Programme: <b>${esc(progLabel)}</b></span>`
  );
}

/**
 * The active-filter summary — a PURE, DOM-free description of what is set, so the
 * chip strip is unit-testable. Returns per-dim chips (dim key, label, first
 * selected value, overflow count), the compare flag, and a period descriptor.
 * esc() is applied at RENDER time, never here — the values are returned raw.
 * @param {any} filters the _filters shape
 * @returns {{dims: Array<{dim: string, label: string, first: string, extra: number, count: number}>, compare: boolean, period: {key: string, label: string, isDefault: boolean}}}
 */
export function activeFilterSummary(filters) {
  const f = filters || {};
  const slice = f.slice || {};
  const dims = [];
  for (const d of SLICE_DIMS) {
    const vals = slice[d.k] || [];
    if (!vals.length) continue;
    dims.push({ dim: d.k, label: d.l, first: vals[0], extra: vals.length - 1, count: vals.length });
  }
  const p = PERIODS.find((x) => x.k === f.period);
  return {
    dims,
    compare: !!f.compare,
    period: { key: f.period || 'all', label: (p && p.l) || 'All time', isDefault: !f.period || f.period === 'all' },
  };
}

/**
 * The on-demand filter panel (Power BI right-pane): a scrim + a docked dialog
 * holding the pinned Clock radiogroup and the 8 slice dims as an accordion. Each
 * EXPANDED group renders the EXISTING sliceMenuHTML listbox verbatim (same
 * checkboxes, per-value counts, roving-tabindex a11y). Active dims are seeded
 * expanded when the panel opens (rpt-filters-open); registry order is preserved
 * (no live re-sort — a group must not jump as you tick its first value).
 * @param {any} raw the report snapshot (for sliceValueCounts)
 */
function filterPanelHTML(raw) {
  const f = _filters;
  const clockRadios = CLOCKS.map((c) => {
    const on = f.clock === c.k;
    return (
      `<button type="button" id="rpt-clockr-${esc(c.k)}" class="rpt-seg-b${on ? ' on' : ''}" role="radio" ` +
      `aria-checked="${on ? 'true' : 'false'}" tabindex="${on ? '0' : '-1'}" data-action="rpt-clock" data-clock="${esc(c.k)}">${esc(c.l)}</button>`
    );
  }).join('');
  const groups = SLICE_DIMS.map((d) => {
    const n = (f.slice[d.k] || []).length;
    const expanded = _openGroups.has(d.k);
    return (
      '<div class="rpt-group">' +
      `<button type="button" id="rpt-grouph-${esc(d.k)}" class="rpt-group-h${expanded ? ' open' : ''}" ` +
      `data-action="rpt-group-toggle" data-dim="${esc(d.k)}" aria-expanded="${expanded ? 'true' : 'false'}" ` +
      `aria-controls="rpt-group-${esc(d.k)}" aria-label="${esc(d.l)}${n ? ', ' + fmtNum(n) + ' selected' : ''}">` +
      ic(IC_CHEVRON, 15) +
      `<span class="rpt-group-t">${esc(d.l)}</span>` +
      (n ? `<span class="rpt-group-n" aria-hidden="true">${esc(fmtNum(n))}</span>` : '') +
      '</button>' +
      `<div class="rpt-group-body" id="rpt-group-${esc(d.k)}"${expanded ? '' : ' hidden'}>` +
      (expanded ? sliceMenuHTML(d, raw) : '') +
      '</div>' +
      '</div>'
    );
  }).join('');
  return (
    '<div class="rpt-scrim" data-action="rpt-filters-open" aria-hidden="true"></div>' +
    '<aside class="rpt-panel" id="rpt-panel-filters" role="dialog" aria-modal="true" aria-label="Report filters" tabindex="-1">' +
    '<div class="rpt-panel-h">' +
    '<span class="rpt-panel-t">Report filters</span>' +
    `<button type="button" id="rpt-panel-x" class="rpt-panel-x" data-action="rpt-filters-open" aria-label="Close filters">${ic(IC_X, 16)}</button>` +
    '</div>' +
    '<div class="rpt-panel-clock">' +
    '<div class="rpt-panel-cap">Which date the period filters on</div>' +
    `<div class="rpt-seg rpt-clock-radio" role="radiogroup" aria-label="Which date the period filters on">${clockRadios}</div>` +
    '</div>' +
    `<div class="rpt-groups">${groups}</div>` +
    '</aside>'
  );
}

function sliceMenuHTML(dim, raw) {
  const vals = sliceValueCounts(raw, dim.k, new Date());
  const sel = _filters.slice[dim.k] || [];
  return (
    `<div class="rpt-menu" id="rpt-menu-${esc(dim.k)}" aria-label="${esc(dim.l)} — choose values to include">` +
    `<div class="rpt-menu-h">${esc(dim.l)} — choose values to include</div>` +
    // ONE tab stop for the whole listbox, navigated with the arrow keys, per the
    // ARIA listbox pattern. Giving each option its own tabindex="0" injected a
    // tab stop per value — a county dimension with 40 values put 40 stops in the
    // page — and without arrow handling there was no other way through it.
    `<div class="rpt-menu-list" id="rpt-menulist-${esc(dim.k)}" role="listbox" aria-multiselectable="true" tabindex="0" data-dim="${esc(dim.k)}"` +
    ` aria-label="${esc(dim.l)} values"${_activeOpt[dim.k] ? ` aria-activedescendant="rpt-opt-${esc(dim.k)}-${esc(optSlug(_activeOpt[dim.k]))}"` : ''}>` +
    (vals.length
      ? vals
          .map((v) => {
            const on = sel.includes(v.v);
            const act = _activeOpt[dim.k] === v.v;
            return (
              `<div class="rpt-opt${on ? ' on' : ''}${act ? ' active' : ''}" id="rpt-opt-${esc(dim.k)}-${esc(optSlug(v.v))}" ` +
              `role="option" aria-selected="${on ? 'true' : 'false'}" tabindex="-1" data-kbd-click ` +
              `data-action="rpt-slice" data-dim="${esc(dim.k)}" data-val="${esc(v.v)}">` +
              `<span class="rpt-opt-box" aria-hidden="true">${on ? '✓' : ''}</span>` +
              `<span class="rpt-opt-l">${esc(v.v)}</span><span class="rpt-num">${esc(fmtNum(v.n))}</span></div>`
            );
          })
          .join('')
      : '<p class="rpt-empty">No values yet.</p>') +
    '</div></div>'
  );
}

/**
 * A DOM-id-safe slug for an option value. Values are free text (county names,
 * facility names, '—'), so they cannot go into an id raw.
 * @param {string} v @returns {string}
 */
function optSlug(v) {
  return String(v).replace(/[^a-zA-Z0-9]+/g, '-').replace(/^-|-$/g, '').toLowerCase() || 'x';
}

// ─── Tiles ─────────────────────────────────────────────────────────────────

/** The ⓘ caveat affordance — the metric's exact definition, from the registry. */
function caveatHTML(m) {
  return `<button type="button" class="rpt-info" data-action="rpt-caveat" data-metric="${esc(m.id)}" title="${esc(m.caveat)}" aria-label="How ${esc(m.label)} is calculated">ⓘ</button>`;
}

function badgeHTML(m) {
  if (m.dataState === DATA_STATE.NEEDS_FIELD) return '<span class="rpt-badge needs">NEEDS FIELD</span>';
  if (m.isNew) return '<span class="rpt-badge new">NEW</span>';
  if (m.isEnhanced) return '<span class="rpt-badge enh">ENHANCED</span>';
  return '';
}

function deckBoxHTML(m) {
  // Needs-field tiles cannot go on a deck — there is nothing to put on the slide.
  if (m.dataState === DATA_STATE.NEEDS_FIELD) return '';
  const on = _deck.has(m.id);
  return (
    // The visible word stays "deck" as the mouse affordance, but the ACCESSIBLE
    // name carries the metric — otherwise ~40 checkboxes all announce "deck,
    // checkbox, not checked" and the one thing the control chooses (which tile
    // goes on the export) is exactly what is not conveyed.
    `<label class="rpt-deckbox"><input type="checkbox" class="rpt-deck" data-metric="${esc(m.id)}"` +
    ` aria-label="Include ${esc(m.label)} in the deck"${on ? ' checked' : ''}/>` +
    '<span>deck</span></label>'
  );
}

/**
 * One tile: header (label, caveat, badges, deck checkbox) + body.
 * @param {any} m metric def @param {any} res computed result @param {any} ctx
 */
function tileHTML(m, res, ctx) {
  const needs = m.dataState === DATA_STATE.NEEDS_FIELD;
  const span = TILE_SPAN[m.id] || 1;
  const body = needs
    ? `<div class="rpt-needs"><p class="rpt-needs-t">Not measurable yet</p><p class="rpt-needs-d">${esc(m.caveat.replace(/^NEEDS FIELD\.\s*/, ''))}</p></div>`
    : (TILE_BODY[m.id] || genericBody)(res, ctx);
  return (
    `<article class="rpt-card${needs ? ' needs' : ''}${m.isNew ? ' isnew' : ''}" data-span="${span}">` +
    '<header class="rpt-card-h">' +
    `<div><h3 class="rpt-card-t">${esc(m.label)} ${caveatHTML(m)}</h3></div>` +
    `<div class="rpt-card-a">${badgeHTML(m)}${deckBoxHTML(m)}</div>` +
    '</header>' +
    `<div class="rpt-card-b">${body}</div>` +
    '</article>'
  );
}

/** Fallback body for a metric with no bespoke renderer. */
function genericBody(res) {
  return bigStat(res.display, res.sub || '');
}

// ─── KPI band ──────────────────────────────────────────────────────────────

function kpiHTML(m, res, prevRes, sparks) {
  const delta = _filters.compare ? metricDelta(m.id, res.value, prevRes && prevRes.value) : null;
  const rag = metricRag(m.id, res.value);
  // The arrow, the percentage and "vs prev" are ONE unbreakable phrase: split
  // across lines, "▲ 8%" over "vs prev" reads as two facts instead of one.
  const deltaHTML = delta
    ? delta.kind === 'new'
      ? `<span class="rpt-deltawrap"><span class="rpt-delta ${delta.good ? 'up' : 'down'}">new</span> vs prev</span>`
      : `<span class="rpt-deltawrap"><span class="rpt-delta ${delta.good ? 'up' : 'down'}">${delta.pct > 0 ? '▲' : delta.pct < 0 ? '▼' : '▬'} ${Math.abs(delta.pct)}%</span> vs prev</span>`
    : '';
  // Compare is ON but this metric cannot honestly be compared — say so once, in
  // place of the delta, rather than leaving a silent gap the reader fills in.
  const noDelta = _filters.compare && !delta && m.deltaSuppressed ? '<span class="rpt-nodelta">no comparable previous period</span>' : '';
  const target = typeof m.target === 'number' ? `<span class="rpt-target">target ${esc(String(m.target))}${m.unit === '%' ? '%' : ''}</span>` : '';
  const spark = m.sparkOf && sparks[m.sparkOf] ? sparkline(sparks[m.sparkOf], sparkColor(m.sparkOf)) : '';
  return (
    `<article class="rpt-kpi${m.isNew ? ' isnew' : ''}${rag ? ' rag-' + rag : ''}">` +
    `<div class="rpt-kpi-l">${esc(m.label)} ${caveatHTML(m)} ${target}</div>` +
    `<div class="rpt-kpi-v">${esc(res.display)}</div>` +
    `<div class="rpt-kpi-d">${deltaHTML}${noDelta}${res.sub ? `<span class="rpt-kpi-s">${esc(res.sub)}</span>` : ''}</div>` +
    (spark ? `<div class="rpt-kpi-spk">${spark}</div>` : '') +
    '</article>'
  );
}

function sparkColor(kind) {
  if (kind === 'cycles') return '#0891B2';
  if (kind === 'seriousAes') return '#B91C1C';
  return '#1F4477';
}

// ─── Pivot (report areas) ────────────────────────────────────────────────────

/** The current location.hash, or '' outside a browser (tests/harness). @returns {string} */
function hashNow() {
  const w = /** @type {any} */ (globalThis);
  return (w.location && w.location.hash) || '';
}

/** The six-tab pivot (WAI-ARIA tablist). The active tab holds the roving tab stop. */
function pivotHTML() {
  const tabs = REPORT_AREAS.map((a) => {
    const meta = AREA_META[a.key] || { icon: '', sub: '' };
    const on = _area === a.key;
    return (
      `<button type="button" role="tab" id="rpt-tab-${esc(a.key)}" class="rpt-tab${on ? ' on' : ''}" ` +
      `data-action="rpt-area" data-area="${esc(a.key)}" aria-selected="${on ? 'true' : 'false'}" ` +
      `aria-controls="rpt-panel" tabindex="${on ? '0' : '-1'}">` +
      ic(meta.icon, 16) +
      `<span>${esc(a.title)}</span></button>`
    );
  }).join('');
  return `<div class="rpt-pivot" role="tablist" aria-label="Report areas" aria-orientation="horizontal">${tabs}</div>`;
}

/**
 * Switch the active area: update state, reflect it in the address bar WITHOUT a
 * hashchange (replaceState fires no onRoute, so no refetch and no skeleton
 * flash), repaint the panel, and land focus on the now-selected tab (the roving
 * tabindex the WAI-ARIA tabs pattern specifies). A click on the already-active
 * tab is a no-op that just re-homes focus.
 * @param {string} key
 */
function switchArea(key) {
  if (!REPORT_AREA_KEYS.includes(key) || key === _area) {
    focusById('rpt-tab-' + _area);
    return;
  }
  _area = key;
  _periodOpen = false;
  _panelOpen = false;
  const w = /** @type {any} */ (globalThis);
  if (w.location && w.history && typeof w.history.replaceState === 'function') {
    // A sandboxed or file: origin can refuse replaceState; the pivot still works,
    // the URL simply does not update — never let it throw the click handler.
    try {
      w.history.replaceState(null, '', '#/reports/' + key);
    } catch (e) {
      console.warn('reports: could not sync the area to the address bar', e);
    }
  }
  render();
  focusById('rpt-tab-' + key);
}

/**
 * Reflect _filters.tier on the persistent command-bar toggle. The tier control
 * lives in the header (OUTSIDE #rpt-root, so it survives area switches), which
 * means render() — which only repaints the panel — cannot restyle it. It is
 * synced by hand here, exactly as setExportsEnabled() maintains the export
 * buttons that also live in the header.
 */
function syncCommandBar() {
  const d = doc();
  const seg = d && d.querySelector('.rpt-tierseg');
  if (!seg) return;
  for (const b of Array.from(seg.querySelectorAll('.rpt-seg-b'))) {
    const el = /** @type {any} */ (b);
    const on = el.getAttribute('data-tier') === _filters.tier;
    el.classList.toggle('on', on);
    el.setAttribute('aria-pressed', on ? 'true' : 'false');
  }
}

// ─── Overview landing ────────────────────────────────────────────────────────

/** The 12-tile KPI band (Overview only) — the full set, tier-independent. */
function kpiBandHTML(agg, prev, sparks) {
  return (
    '<section class="rpt-kpis" aria-label="Headline indicators">' +
    kpiMetrics()
      .map((m) => kpiHTML(m, agg[m.id] || { display: '—', value: null }, prev && prev[m.id], sparks))
      .join('') +
    '</section>'
  );
}

/**
 * The status chip on an area scorecard: a RAG signal where the area owns a metric
 * with a target, a plain fact otherwise. metricRag returns null (→ neutral) for a
 * value we do not have — never a green light for a missing number.
 * @param {string} key @param {Record<string, any>} agg
 * @returns {{cls: string, label: string}}
 */
function scorecardChip(key, agg) {
  const disp = (id) => (agg[id] && agg[id].display) || '—';
  const val = (id) => (agg[id] ? agg[id].value : null);
  const rag = (id) => metricRag(id, val(id)) || 'neutral';
  if (key === 'enrolment') return { cls: 'neutral', label: fmtNum(val('active')) + ' active now' };
  if (key === 'access') return { cls: rag('time_to_treatment'), label: 'Time to treatment ' + disp('time_to_treatment') };
  if (key === 'treatment') {
    const ae = val('serious_ae_mtd');
    if (ae) return { cls: 'danger', label: fmtNum(ae) + ' serious AE' + (ae === 1 ? '' : 's') + ' — review' };
    return { cls: rag('refills_on_time'), label: 'Refills on time ' + disp('refills_on_time') };
  }
  if (key === 'retention') return { cls: rag('ltfu'), label: 'Lost to follow-up ' + disp('ltfu') };
  if (key === 'operations') return { cls: 'neutral', label: disp('cost_per_patient') + ' per patient' };
  return { cls: 'neutral', label: '' };
}

/**
 * The five at-a-glance area scorecards on Overview. Each is a whole-card button
 * that deep-links into its area, reusing the agg map render() already computed —
 * no second compute. Each id is resolved through getMetric() so a typo drops the
 * stat rather than crashing the card.
 * @param {Record<string, any>} agg @returns {string}
 */
function scorecardsHTML(agg) {
  const cards = REPORT_AREAS.filter((a) => a.key !== 'overview')
    .map((a) => {
      const meta = AREA_META[a.key] || { icon: '', sub: '' };
      const kpis = (SCORECARD[a.key] || [])
        .map((id) => {
          const m = getMetric(id);
          if (!m) return '';
          const r = agg[id] || { display: '—' };
          return `<span class="rpt-sc-kpi"><b>${esc(r.display)}</b><i>${esc(SC_LABEL[id] || m.label)}</i></span>`;
        })
        .join('');
      const chip = scorecardChip(a.key, agg);
      return (
        `<button type="button" class="rpt-scorecard" data-action="rpt-area" data-area="${esc(a.key)}" aria-label="Open the ${esc(a.title)} area">` +
        `<span class="rpt-sc-top">${ic(meta.icon, 19)}<span class="rpt-sc-t">${esc(a.title)}</span>${ic(IC_ARROW, 15)}</span>` +
        (meta.sub ? `<span class="rpt-sc-sub">${esc(meta.sub)}</span>` : '') +
        `<span class="rpt-sc-kpis">${kpis}</span>` +
        (chip.label ? `<span class="rpt-sc-chip ${esc(chip.cls)}">${esc(chip.label)}</span>` : '') +
        '</button>'
      );
    })
    .join('');
  return '<div class="rpt-sc-h2">Explore by area</div><div class="rpt-scorecards">' + cards + '</div>';
}

/**
 * One area's section grids. The area's KPI tiles LEAD their home section (no
 * kpi:false filter), so every headline number is reachable inside its area as
 * well as on Overview — the reachability the mapping test asserts.
 * @param {string} areaKey @param {Record<string, any>} agg @param {any} ctx
 * @returns {string}
 */


function areaHTML(areaKey, agg, ctx) {
  const area = REPORT_AREAS.find((a) => a.key === areaKey);
  if (!area) return '';
  const out = [];
  for (const sid of area.sections) {
    const s = SECTIONS.find((x) => x.id === sid);
    if (!s) continue;
    const metrics = metricsFor({ section: sid, tier: _filters.tier });
    if (!metrics.length) continue;
    out.push(
      `<div class="rpt-sec-h"><h2>${esc(s.title)}</h2>${s.isNew ? '<span class="rpt-badge new">NEW SECTION</span>' : ''}<span class="rpt-ln"></span></div>` +
        `<section class="rpt-grid" aria-label="${esc(s.title)}">` +
        metrics.map((m) => tileHTML(m, agg[m.id] || { display: '—', value: null }, ctx)).join('') +
        '</section>'
    );
  }
  // Reports carries NO admin-only panels. Both that used to close Operations —
  // sign-in activity and Training performance — were removed on Jimmy's
  // instruction (2026-08-14): this report shows programme figures, not views
  // that only administrators can see. Every reader of this page now sees the
  // same page. The underlying rows are untouched: the login audit still records
  // sign-ins, and training_events / training_answers still record progress.
  const si = '';
  if (!out.length) {
    return si
      ? '<p class="rpt-empty">No tiles in this area at the current detail level.</p>' + si
      : '<p class="rpt-empty">No tiles in this area at the current detail level.</p>';
  }
  return out.join('') + si;
}

// ─── Render ────────────────────────────────────────────────────────────────

function skeleton() {
  const f = _filters;
  // Command bar (tier depth control + exports) lives in the header, OUTSIDE
  // #rpt-root, so it survives every area switch. The tier control reads as a
  // DEPTH axis here — Board vs Analyst detail — deliberately separate from the
  // pivot tabs, which are navigation. Its active state is kept current by
  // syncCommandBar() on each render (render only repaints the panel).
  const tierSeg =
    '<div class="rpt-seg rpt-tierseg" role="group" aria-label="Detail level" ' +
    'title="Board = curated decision tiles · Analyst = every breakdown">' +
    segBtn('Board', TIER.BOARD, f.tier, 'rpt-tier', 'tier') +
    segBtn('Analyst', TIER.ANALYST, f.tier, 'rpt-tier', 'tier') +
    '</div>';
  return (
    '<div id="reports-view" class="rpt">' +
    '<header class="rpt-head">' +
    '<div><h1 class="rpt-h">Programme <span>Reports</span></h1>' +
    '<p class="rpt-meta">Enrolment, access, adherence, safety, financing and equity — sliceable on every dimension</p></div>' +
    '<div class="rpt-head-a">' +
    tierSeg +
    `<button type="button" class="btn" id="rpt-btn-csv" data-action="rpt-csv">${ic(IC_DOWN, 15)}Export CSV</button>` +
    `<button type="button" class="btn pri" id="rpt-btn-ppt" data-action="rpt-ppt">${ic(IC_DOWN, 15)}PowerPoint</button>` +
    '</div>' +
    '</header>' +
    '<div id="rpt-root"><div class="rpt-skel"></div><div class="rpt-skel"></div><div class="rpt-skel"></div></div>' +
    '</div>'
  );
}

/**
 * Enable/disable the two header export buttons. They sit outside #rpt-root, so
 * they survive every re-render and must be gated by hand.
 * @param {boolean} on
 */
function setExportsEnabled(on) {
  const d = doc();
  if (!d) return;
  for (const id of ['rpt-btn-csv', 'rpt-btn-ppt']) {
    const b = d.getElementById(id);
    if (b) /** @type {any} */ (b).disabled = !on;
  }
}

function render() {
  const d = doc();
  const root = d && d.getElementById('rpt-root');
  if (!root) return;

  // The whole of #rpt-root is about to be replaced, detaching whatever held focus
  // inside it. Remember its id so we can put focus back if it survives the swap —
  // this keeps focus alive across a background re-render (a realtime tick while
  // the filter panel is open). Action handlers focus AFTER render() returns, so
  // their explicit focus still wins; this only rescues focus that would otherwise
  // fall to <body>.
  const focusedId = d.activeElement && d.activeElement.id ? d.activeElement.id : '';

  // Reconcile the persistent header tier toggle FIRST, before any early return.
  // It lives outside #rpt-root, so nothing else repaints it; if a user flips the
  // tier while the panel is showing the error or loading state (both of which
  // return early below), this is the only thing that keeps the control's `on`
  // class and aria-pressed honest.
  syncCommandBar();

  // Every re-render replaces the canvases; destroy the old Chart instances first
  // or they keep their (now detached) canvases alive and re-attach tooltips.
  destroyCharts();

  const err = reportsError();
  // The export buttons live in the persistent header, OUTSIDE #rpt-root, so
  // render() cannot remove them — it has to disable them explicitly, or they stay
  // live over an error screen and hand out a stale artefact.
  setExportsEnabled(!err);
  if (err || !reportsRaw() || !reportsLoaded()) {
    // The panel/popover lose their DOM to the error/skeleton state, so drop their
    // open flags too — otherwise a surface latched open here would pop back up
    // (scrim and all) on the next successful render.
    _panelOpen = false;
    _periodOpen = false;
  }
  if (err) {
    root.innerHTML =
      '<div class="rpt-error" role="alert"><p class="rpt-error-t">This report did not load.</p>' +
      '<p class="rpt-error-d">No figures are shown, because a partly-loaded report looks exactly like a true one. ' +
      'Check your connection and try again.</p>' +
      '<button type="button" class="btn pri" data-action="rpt-retry">Try again</button></div>';
    return;
  }

  const raw = reportsRaw();
  if (!raw || !reportsLoaded()) {
    root.innerHTML = '<div class="rpt-skel"></div><div class="rpt-skel"></div><div class="rpt-skel"></div>';
    return;
  }

  const now = new Date();
  const programmes = programmeList();
  const rows = applyFilters(raw, _filters, { now });
  const agg = aggregate(rows, { now, programmes });
  const prev = comparePrevious(raw, _filters, { now, programmes });

  // Sparkline series: last 12 months of the underlying stream, computed here
  // (presentation), never in a metric — a spark is a shape, not a number.
  const sparks = {
    cases: monthlySeries(rows.cases, 12, now),
    cycles: monthlySeries(rows.cycles, 12, now),
    seriousAes: monthlySeries(rows.aes.filter((a) => a && a.seriousness === 'serious'), 12, now),
  };

  const parts = [];

  // Truthful cap banner (R-013) — only when a stream really came back short.
  const trunc = reportsTruncated();
  if (trunc) {
    parts.push(
      '<div class="rpt-banner warn" role="status"><b>These figures are incomplete.</b> ' +
        esc(
          trunc.streams
            .map((s) => `${s.name}: ${fmtNum(s.got)} of ${fmtNum(s.total)} rows loaded`)
            .join(' · ')
        ) +
        '. Totals and every rate below are understated. Reload before quoting them.</div>'
    );
  }

  // Sticky chrome: the filter bar AND the pivot freeze together just below the app
  // top bar, so the tabs stay reachable while a long report scrolls under them.
  // .rpt-chrome is the sticky element (its parent #rpt-root is the full-height
  // scroll content, so it stays stuck the whole way down); the filter bar and
  // pivot inside it are NOT individually sticky.
  parts.push('<div class="rpt-chrome">');
  parts.push(filterBarHTML());
  // The pivot: navigation across report areas, frozen with the filter bar above it.
  parts.push(pivotHTML());
  parts.push('</div>');

  // The filter panel/scrim live OUTSIDE the sticky chrome (its stacking context
  // would otherwise trap the fixed panel below the top bar).
  parts.push(_panelOpen ? filterPanelHTML(raw) : '');

  // The tab panel holds ONLY the active area — the direct fix for the 51-tile
  // scroll and the heavy DOM on a low-end phone. Overview is the landing (exec
  // narrative + full KPI band + one scorecard per area); every other tab shows
  // its own sections, with each area's KPI tiles leading their home section.
  parts.push(
    `<div class="rpt-area" id="rpt-panel" role="tabpanel" aria-labelledby="rpt-tab-${esc(_area)}" tabindex="-1">`
  );
  if (_area === 'overview') {
    parts.push(execHTML(agg, prev, rows, now, programmes));
    parts.push(kpiBandHTML(agg, prev, sparks));
    parts.push(scorecardsHTML(agg));
  } else {
    parts.push(areaHTML(_area, agg, { now }));
  }
  parts.push('</div>');

  // Export privacy guard — a standing statement, not a metric.
  parts.push(
    '<div class="rpt-privacy"><b>What leaves this page.</b> Exports carry aggregated counts only; ' +
      'the at-risk patient list stays in the app, role-restricted. Every export is stamped with the filters used and the date. ' +
      '(Kenya Data Protection Act 2019.)</div>'
  );

  parts.push(
    // The live region wraps the TEXT only: a role="status" around the button
    // re-announced "Build deck" on every repaint (A-12).
    `<div class="rpt-deckbar"><span role="status">Deck builder: <b id="rpt-deckn">${esc(fmtNum(_deck.size))}</b> tile${_deck.size === 1 ? '' : 's'} selected` +
      `<span class="rpt-deckbar-n" id="rpt-deckhint">${_deck.size ? 'Only these tiles go on the deck' : 'Tick nothing and the deck carries everything'}</span></span>` +
      `<button type="button" class="btn sm pri" data-action="rpt-ppt">${ic(IC_DOWN, 14)}Build deck</button></div>`
  );

  root.innerHTML = parts.join('');

  // Put focus back if the swap dropped it to <body> and the same element is still
  // here (a background re-render preserving the user's place). Only acts on a
  // fallen-to-body focus, so it never fights an action handler's own focus call.
  if (focusedId && d.activeElement === d.body) {
    const keep = d.getElementById(focusedId);
    if (keep && typeof keep.focus === 'function') keep.focus();
  }

  // Geometry BEFORE charts — .rpt-canvas-wrap gets its height here, and Chart.js
  // sizes itself to a wrapper that must already be non-zero.
  applyGeometry(root);

  // Charts last — the canvases must exist before Chart.js is pointed at them.
  const chartsOK = !!getChart();
  if (chartsOK) {
    paintCharts(agg);
    describeCharts();
  } else if (!_chartsWarned) {
    _chartsWarned = true;
    toast('Charts unavailable — the chart script did not load. Every figure is still shown as text.', 'err');
  }
}

/**
 * Apply every data-driven dimension from JS. The deployed CSP is
 * `style-src 'self'` with no 'unsafe-inline' and no style-src-attr, which blocks
 * inline `style=` ATTRIBUTES as well as <style> blocks — so a percentage width
 * written into the markup is silently dropped in production while working
 * perfectly in the headless harness (which serves without _headers). Styles set
 * through the CSSOM are not restricted by CSP, which is why the rest of the
 * codebase already does it this way (board.js, patients.js, consents.js).
 * @param {any} root
 */
function applyGeometry(root) {
  if (!root || typeof root.querySelectorAll !== 'function') return;
  /** @param {string} sel @param {(el: any, v: string) => void} fn @param {string} attr */
  const each = (sel, attr, fn) => {
    for (const el of Array.from(root.querySelectorAll(sel))) {
      const v = /** @type {any} */ (el).getAttribute(attr);
      if (v !== null) fn(el, v);
    }
  };
  each('.rpt-canvas-wrap[data-h]', 'data-h', (el, v) => {
    el.style.height = (Number(v) || 0) + 'px';
  });
  each('.rpt-card[data-span]', 'data-span', (el, v) => {
    el.style.gridColumn = 'span ' + (Number(v) || 1);
  });
  each('.rpt-hbar-f[data-w]', 'data-w', (el, v) => {
    el.style.width = v + '%';
  });
  each('.rpt-gauge-f[data-w]', 'data-w', (el, v) => {
    el.style.width = v + '%';
  });
  each('.rpt-si-col[data-h]', 'data-h', (el, v) => {
    el.style.height = (Number(v) || 0) + '%';
  });
  each('.rpt-gauge-t[data-left]', 'data-left', (el, v) => {
    el.style.left = v + '%';
  });
  each('[data-bg]', 'data-bg', (el, v) => {
    el.style.background = v;
  });
  each('td[data-heat]', 'data-heat', (el, v) => {
    el.style.background = 'rgba(31,68,119,' + v + ')';
    if (el.getAttribute('data-heat-ink')) el.style.color = '#fff';
  });
}

/**
 * Fill each chart's visually-hidden summary with the same rule-based one-liner
 * the deck puts under its chart slides. Without it a canvas-only tile has no
 * text alternative at all and the whole insight is locked in pixels.
 */
function describeCharts() {
  const d = doc();
  if (!d) return;
  for (const m of deckChartMetrics()) {
    const p = d.getElementById('rptc-sum-' + m.id);
    if (!p) continue;
    const s = chartSeriesOf(m.id);
    p.textContent = s ? chartTakeaway(s) : '';
  }
}

function execHTML(agg, prev, rows, now, programmes) {
  const enrolled = (agg.enrolled && agg.enrolled.value) || 0;
  if (!enrolled) {
    return (
      '<div class="rpt-exec empty"><h2>No patients in the current view</h2>' +
      '<p>Nothing matched these filters. Widen the period or clear a slice chip — this is an empty selection, not an empty programme.</p></div>'
    );
  }
  // Narrative inputs that are presentation facts (a name, a ranking) rather than
  // metrics live here; everything numeric comes from the registry results.
  /** @type {Record<string, number>} */
  const perProg = {};
  for (const c of rows.cases) perProg[String(c.programme_id)] = (perProg[String(c.programme_id)] || 0) + 1;
  const topProgId = Object.keys(perProg).sort((a, b) => perProg[b] - perProg[a])[0];
  const topProgObj = programmes.find((p) => p && String(p.programme_id) === topProgId);
  const topProgramme =
    topProgObj && Object.keys(perProg).length > 1 ? `${topProgObj.display_name} (${fmtNum(perProg[topProgId])})` : null;
  const counties = (agg.patients_by_county && agg.patients_by_county.data && agg.patients_by_county.data.items) || [];
  const topCounties = counties.slice(0, 3).map((i) => i.l);

  const periodLabel = (PERIODS.find((p) => p.k === _filters.period) || {}).l || 'All time';
  const clauses = execClauses(agg, prev, { periodLabel, filtered: rows.filtered, topProgramme, topCounties });

  const headline = [
    { id: 'enrolled', l: 'Patients in view' },
    { id: 'refills_on_time', l: 'Refills on time (proxy)' },
    { id: 'time_to_treatment', l: 'Time to treatment' },
    { id: 'serious_ae_mtd', l: 'Serious AEs (MTD)' },
  ];

  return (
    '<section class="rpt-exec" aria-label="Executive summary">' +
    `<h2>Executive summary — ${esc(periodLabel)}</h2>` +
    `<p class="rpt-exec-sub">Rule-based narrative computed from the filtered data — no text is generated, every clause is a template filled from a registry metric.` +
    `${rows.filtered ? ' · filters active' : ''}</p>` +
    '<div class="rpt-exec-row">' +
    headline
      .map((h) => {
        const r = agg[h.id] || { display: '—', sub: '' };
        return `<div class="rpt-exec-m"><div class="v">${esc(r.display)}</div><div class="l">${esc(h.l)}</div><div class="dl">${esc(r.sub || '')}</div></div>`;
      })
      .join('') +
    '</div>' +
    (clauses.length ? `<p class="rpt-exec-p">${clauses.map((c) => esc(c)).join(' ')}</p>` : '') +
    '</section>'
  );
}

// ─── CSV export (registry-driven, csvSafe every cell) ──────────────────────

/**
 * Build the export rows from the registry — the SAME compute() the screen used,
 * so a CSV can never disagree with the tile above it. Patient-level metrics
 * (exportable === false) are omitted: exports carry aggregates only.
 * @param {Record<string, any>} agg
 * @param {Record<string, any>|null} prev
 * @param {{periodLabel: string, clockLabel: string, programme: string, sliceLines: string[], generatedAt: string}} scope
 * @returns {string[][]}
 */
export function buildCsvRows(agg, prev, scope) {
  const rows = [];
  rows.push(['Tumaini PSP — programme report']);
  rows.push(['Generated', scope.generatedAt]);
  rows.push(['Period', scope.periodLabel]);
  rows.push(['Clock', scope.clockLabel]);
  rows.push(['Programme', scope.programme]);
  for (const l of scope.sliceLines) rows.push(['Filter', l]);
  rows.push([]);
  rows.push(['Metric', 'Value', 'Unit', 'Target', 'vs previous', 'Data state', 'Definition']);
  for (const m of metricsFor({})) {
    if (m.exportable === false) continue;
    const r = agg[m.id] || { display: '—', value: null };
    const d = prev ? metricDelta(m.id, r.value, prev[m.id] && prev[m.id].value) : null;
    rows.push([
      m.label,
      m.dataState === DATA_STATE.NEEDS_FIELD ? 'not measurable' : r.display,
      m.unit || '',
      typeof m.target === 'number' ? String(m.target) : '',
      d ? (d.kind === 'new' ? 'new' : (d.pct > 0 ? '+' : '') + d.pct + '%') : '',
      m.dataState,
      m.caveat,
    ]);
  }
  return rows;
}

/** Serialise rows to a CSV string, csvSafe()-ing every cell. @param {string[][]} rows @returns {string} */
export function toCsv(rows) {
  return rows.map((r) => r.map((v) => '"' + csvSafe(v).replace(/"/g, '""') + '"').join(',')).join('\n');
}

/**
 * The scope stamp for an export: what the numbers are a view OF. Shared by the
 * CSV and the deck so the two can never describe the same export differently.
 * @param {Date} now
 * @returns {{periodLabel: string, clockLabel: string, programme: string, sliceLines: string[], generatedAt: string}}
 */
function exportScope(now) {
  const prog = store.get(SLICES.PROGRAMMES) || {};
  const activeProg = (store.get(SLICES.FILTERS) || {}).programme || 'all';
  const sliceLines = [];
  for (const d of SLICE_DIMS) {
    const sel = _filters.slice[d.k] || [];
    if (sel.length) sliceLines.push(d.l + ': ' + sel.join(', '));
  }
  return {
    periodLabel: (PERIODS.find((p) => p.k === _filters.period) || {}).l || 'All time',
    clockLabel: (CLOCKS.find((c) => c.k === _filters.clock) || {}).l || 'Enrolment',
    programme: pillLabel(activeProg, prog.byCode || {}),
    sliceLines,
    // LOCAL parts, not toISOString(). Every period boundary in this app is built
    // from local date parts; a UTC stamp names the previous calendar day for any
    // export run before 03:00 in Nairobi, and deckFileName() already uses local
    // parts — so the cover slide and the filename disagreed about the date of the
    // same run. This is the provenance line a reader uses to tell two artefacts
    // apart, so it follows the same clock as everything it describes.
    generatedAt: localStamp(now),
  };
}

/** `YYYY-MM-DD HH:MM` from LOCAL date parts. @param {Date} d @returns {string} */
export function localStamp(d) {
  const p = (/** @type {number} */ n) => String(n).padStart(2, '0');
  return (
    d.getFullYear() + '-' + p(d.getMonth() + 1) + '-' + p(d.getDate()) + ' ' + p(d.getHours()) + ':' + p(d.getMinutes())
  );
}

function onExportCsv() {
  // The error gate comes FIRST and is not merely belt-and-braces: the export
  // buttons live in the persistent header, which render() never replaces, so
  // they stay clickable while the page reads "This report did not load".
  if (reportsError()) {
    toast('This report did not load — nothing to export', 'err');
    return;
  }
  const raw = reportsRaw();
  if (!raw) {
    toast('Nothing to export yet — the report has not loaded', 'err');
    return;
  }
  try {
    const now = new Date();
    const programmes = programmeList();
    const rows = applyFilters(raw, _filters, { now });
    const agg = aggregate(rows, { now, programmes });
    const prev = comparePrevious(raw, _filters, { now, programmes });
    const csv = toCsv(buildCsvRows(agg, prev, exportScope(now)));
    const d = doc();
    if (!d) return;
    // BOM so Excel opens the UTF-8 correctly on a Kenyan Windows locale.
    const blob = new Blob(['﻿' + csv], { type: 'text/csv' });
    const a = d.createElement('a');
    // Local parts, so the filename agrees with the generatedAt stamp inside it.
    const url = URL.createObjectURL(blob);
    a.href = url;
    a.download = 'tumaini-report-' + localStamp(now).slice(0, 10) + '.csv';
    a.click();
    // Revoke, or the blob is pinned for the tab's lifetime — a long clinic session
    // exporting a large filtered report accumulates them. Same pattern as
    // src/views/patients.js. The timeout lets the download start first.
    setTimeout(() => URL.revokeObjectURL(url), 0);
    toast('CSV downloaded', 'ok');
  } catch (e) {
    toast(netErr(e, 'Could not build the CSV'), 'err');
  }
}

// ─── PowerPoint deck (Phase 5B) ────────────────────────────────────────────

/**
 * Lift the plain series descriptor out of a live Chart.js instance, for the
 * rule-based takeaway. Reading the INSTANCE rather than re-deriving from the
 * metric result means the takeaway describes exactly the picture on the slide,
 * including any series the chart config dropped.
 * @param {string} metricId
 * @returns {{type: string, labels: any[], values: number[], seriesCount: number}|null}
 */
function chartSeriesOf(metricId) {
  const ch = _charts.get('rptc-' + metricId);
  if (!ch || !ch.data) return null;
  const sets = ch.data.datasets || [];
  return {
    type: (ch.config && ch.config.type) || '',
    labels: ch.data.labels || [],
    values: ((sets[0] && sets[0].data) || []).map((/** @type {any} */ v) => Number(v) || 0),
    seriesCount: sets.length,
  };
}

/**
 * Snapshot every rendered chart canvas as a PNG data URL. Canvases that are
 * absent, zero-sized (a Board-tier deck: the Analyst tiles were never in the
 * DOM) or that refuse toDataURL are SKIPPED — the deck simply carries fewer
 * chart slides, rather than a slide with an empty frame where a chart should be.
 * @returns {{available: string[], images: Record<string, {dataUrl: string, w: number, h: number}>, series: Record<string, any>}}
 */
function captureCharts() {
  const d = doc();
  /** @type {string[]} */
  const available = [];
  /** @type {Record<string, any>} */
  const images = {};
  /** @type {Record<string, any>} */
  const series = {};
  if (!d) return { available, images, series };
  for (const m of deckChartMetrics()) {
    const el = d.getElementById('rptc-' + m.id);
    if (!el || !el.width || el.width < 20 || el.height < 20) continue;
    let dataUrl = '';
    try {
      dataUrl = el.toDataURL('image/png');
    } catch (e) {
      console.warn('deck: canvas capture failed', m.id, e);
      continue;
    }
    if (!dataUrl || dataUrl.length < 200) continue; // a blank/failed render
    available.push(m.id);
    images[m.id] = { dataUrl, w: el.width, h: el.height };
    const s = chartSeriesOf(m.id);
    if (s) series[m.id] = s;
  }
  return { available, images, series };
}

/** Toggle the deck button's busy state (label restored from data-lbl). @param {boolean} busy @returns {void} */
function setDeckBusy(busy) {
  const d = doc();
  const btn = d && d.getElementById('rpt-btn-ppt');
  if (!btn) return;
  btn.disabled = busy;
  if (busy) {
    if (btn.dataset.lbl === undefined) btn.dataset.lbl = btn.textContent;
    btn.textContent = 'Building deck…';
  } else if (btn.dataset.lbl !== undefined) {
    btn.textContent = btn.dataset.lbl;
    delete btn.dataset.lbl;
  }
}

/**
 * Build the deck from the CURRENT filtered state, WITHOUT writing a file.
 *
 * Split out from onExportDeck so the whole in-browser half — real canvases, real
 * capture, real registry results, real pptxgenjs — can be exercised headless.
 * (The file write itself is covered by the node smoke test; headless Chrome
 * cannot take a download without destabilising, so proving the two halves
 * separately is how this path is actually verified rather than assumed.)
 *
 * @param {Date} [when]
 * @returns {{plan: any[], pptx: any, fileName: string}}
 */
export function prepareDeck(when) {
  // ALL-OR-NOTHING reaches the export paths, not just the screen. A deck built
  // from a stale snapshot after a failed refresh is stamped with a FRESH
  // generatedAt and is indistinguishable from a current one once forwarded.
  if (reportsError()) throw new Error('This report did not load — nothing to export');
  const now = when instanceof Date && !isNaN(when.getTime()) ? when : new Date();
  const programmes = programmeList();
  const raw = reportsRaw();
  if (!raw) throw new Error('The report has not loaded — nothing to export');
  const rows = applyFilters(raw, _filters, { now });
  const agg = aggregate(rows, { now, programmes });
  const prev = comparePrevious(raw, _filters, { now, programmes });
  const scope = exportScope(now);
  const caps = captureCharts();
  const session = store.get(SLICES.SESSION) || {};

  const plan = buildSlidePlan({
    agg,
    prev,
    filters: _filters,
    selection: [..._deck],
    availableCharts: caps.available,
    chartSeries: caps.series,
    ctx: {
      periodLabel: scope.periodLabel,
      clockLabel: scope.clockLabel,
      programmeLabel: scope.programme,
      sliceLines: scope.sliceLines,
      generatedAt: scope.generatedAt,
      generatedBy: session.fullName || null,
      clauses: execClauses(agg, prev, {
        periodLabel: scope.periodLabel,
        filtered: rows.filtered,
        topProgramme: null,
        topCounties: [],
      }),
    },
  });

  // renderDeck runs the identifier assertion against the LIVE dataset and throws
  // rather than building a deck that would carry a patient's name.
  const pptx = renderDeck(plan, {
    images: caps.images,
    author: session.fullName || null,
    dataset: { cases: rows.cases },
  });
  return { plan, pptx, fileName: deckFileName(now) };
}

/**
 * Build and download the branded deck from the CURRENT filtered state.
 * @returns {Promise<void>}
 */
async function onExportDeck() {
  // Offline guard — the same shape as the Chart.js guard, and NEVER a silent
  // no-op: a button that does nothing when clicked is read as "it worked".
  if (!getPptxGen()) {
    toast('PowerPoint export unavailable — the deck script did not load. Reload when online.', 'err');
    return;
  }
  if (reportsError()) {
    toast('This report did not load — nothing to export', 'err');
    return;
  }
  const raw = reportsRaw();
  if (!raw || !reportsLoaded()) {
    toast('The report is still loading — try again in a moment', 'err');
    return;
  }
  setDeckBusy(true);
  try {
    const { plan, pptx, fileName } = prepareDeck();
    await pptx.writeFile({ fileName });
    const n = plan.filter((s) => s.kind === 'chart').length;
    toast(`Deck downloaded — ${fmtNum(plan.length)} slides, ${fmtNum(n)} charts`, 'ok');
  } catch (e) {
    console.warn('onExportDeck', e);
    toast(netErr(e, 'Could not build the deck'), 'err');
  } finally {
    setDeckBusy(false);
  }
}

// ─── Load / refresh ────────────────────────────────────────────────────────



async function refresh() {
  // In PARALLEL, not in sequence: the sign-in audit is a second round trip and
  // chaining it would delay every figure on the page behind a side panel.
  const [res] = await fetchReportData().then((r) => [r]);
  if (res && res.stale) return;
  if (res && !res.ok) {
    toast(netErr(res.error, "Couldn't load the report — try again"), 'err');
  }
  if (mounted()) render();
  // Returned so Sync never announces "Refreshed" over a report that failed to
  // load. `reported: true` because the toast above already told the user —
  // the registry passes the flag on so Sync stays quiet rather than stacking a
  // second toast about the same failure.
  return res && !res.ok ? Object.assign({}, res, { reported: true }) : res;
}

// ─── Wiring ────────────────────────────────────────────────────────────────

/**
 * Close the open slice menu and RETURN FOCUS to the chip that owns it. render()
 * replaces the whole filter bar, so without this the user is dropped to <body> —
 * Escape becomes a way to lose your place rather than back out of a menu.
 * @param {string} [dim] the menu being closed; defaults to the open one
 */
/**
 * Close whichever filter surface is open (the filters panel or the period
 * popover) and RETURN FOCUS to its trigger — the same discipline the old inline
 * slice menu used, so an Escape or outside-click never drops the user to <body>.
 */
function closeSurface() {
  if (_panelOpen) {
    _panelOpen = false;
    render();
    focusById('rpt-filters-btn');
  } else if (_periodOpen) {
    _periodOpen = false;
    render();
    focusById('rpt-period-btn');
  }
}

/** The listbox container for a dimension. @param {string} dim @returns {any} */
function listOf(dim) {
  const d = doc();
  const menu = d && d.getElementById('rpt-menu-' + dim);
  return (menu && menu.querySelector('.rpt-menu-list')) || null;
}

/** Focus an element by id, if it is there. @param {string} id */
function focusById(id) {
  const d = doc();
  const el = d && d.getElementById(id);
  if (el && typeof (/** @type {any} */ (el).focus) === 'function') /** @type {any} */ (el).focus();
}

/**
 * Tab-step for the filters panel. The panel is modal over its scrim, but unlike
 * ui/modal.js's dialog it renders INSIDE #app (it is part of the view), so it
 * cannot mark the page behind it inert. That makes trapStep insufficient on its
 * own: trapStep answers null for a forward Tab from OUTSIDE the dialog, because
 * a modal never meets that case -- inert leaves nothing outside tabbable. Here
 * the page behind the scrim is still tabbable, and null is exactly how focus
 * escaped under it. The wrap behaviour INSIDE the panel is delegated to trapStep
 * so there is one definition of it rather than a second that can drift.
 * @param {boolean} open is the panel open
 * @param {boolean} shiftKey
 * @param {number} activeIndex index of the focused control within the panel, -1 when focus is outside
 * @param {number} count focusable controls in the panel
 * @returns {number|null} index to force focus to, -2 = focus the panel itself, null = let Tab proceed
 */
export function panelTabStep(open, shiftKey, activeIndex, count) {
  if (!open) return null;
  if (count <= 0) return -2;
  if (activeIndex < 0) return shiftKey ? count - 1 : 0;
  return trapStep(shiftKey, activeIndex, count);
}

function wireOnce() {
  if (_wired) return;
  _wired = true;

  register('rpt-retry', () => {
    store.set(SLICES.REPORTS, { loadError: null });
    render();
    void refresh();
  });
  register('rpt-period', (el) => {
    const p = el.getAttribute('data-period');
    if (!p) return;
    _filters.period = p;
    // 'All time' has no previous period — a stale compare flag would advertise a
    // comparison the page is not making.
    if (p === 'all') _filters.compare = false;
    // A preset closes the popover; Custom… keeps it open to reveal the date
    // inputs (committed by the Apply button, not per keystroke).
    if (p !== 'custom') _periodOpen = false;
    render();
    // Custom keeps the popover open, so move focus onto the newly-revealed From
    // input rather than leaving it on <body> after the wholesale re-render.
    focusById(p === 'custom' ? 'rpt-from' : 'rpt-period-btn');
  });
  register('rpt-period-open', () => {
    _periodOpen = !_periodOpen;
    _panelOpen = false; // single-open invariant across the two surfaces
    render();
    if (_periodOpen) {
      const dd = doc();
      const pop = dd && dd.getElementById('rpt-period-pop');
      const first = pop && (pop.querySelector('.rpt-period-opt.on') || pop.querySelector('.rpt-period-opt'));
      if (first && typeof first.focus === 'function') first.focus();
    } else {
      focusById('rpt-period-btn');
    }
  });
  register('rpt-period-custom-apply', () => {
    // Refuse to commit a range that resolves to null (empty or reversed). Otherwise
    // the button would advertise a window the report is not applying (the numbers
    // would silently be All-time). Keep the popover open and say why.
    const w = periodRange('custom', new Date(), _filters.custom || {});
    if (!w) {
      toast('Enter a start and end date, with the end on or after the start.', 'err');
      return;
    }
    _filters.period = 'custom';
    _periodOpen = false;
    render();
    focusById('rpt-period-btn');
  });
  register('rpt-clock', (el) => {
    const c = el.getAttribute('data-clock');
    if (!c) return;
    _filters.clock = c;
    render();
    if (_panelOpen) focusById('rpt-clockr-' + c);
  });
  register('rpt-tier', (el) => {
    const t = el.getAttribute('data-tier');
    if (!t) return;
    _filters.tier = t;
    render();
  });
  register('rpt-area', (el) => {
    const key = el.getAttribute('data-area');
    if (key) switchArea(key);
  });
  register('rpt-compare', () => {
    // A custom range that resolves to null is All-time in effect, so it has no
    // previous period either — treat it the same as 'all' so Compare cannot be
    // switched on to advertise a comparison the page never computes.
    const noWindow =
      _filters.period === 'all' ||
      (_filters.period === 'custom' && !periodRange('custom', new Date(), _filters.custom || {}));
    if (!_filters.compare && noWindow) {
      toast('Pick a period first — this view has no previous period to compare against', 'err');
      return;
    }
    _filters.compare = !_filters.compare;
    render();
    focusById('rpt-compare-btn');
  });
  register('rpt-filters-open', () => {
    _panelOpen = !_panelOpen;
    _periodOpen = false; // single-open invariant
    if (_panelOpen) {
      // Seed the accordion: active dims start expanded so what is set is seen
      // first; they can still be collapsed (toggle removes them from the set).
      _openGroups = new Set(SLICE_DIMS.filter((d) => (_filters.slice[d.k] || []).length).map((d) => d.k));
    }
    render();
    focusById(_panelOpen ? 'rpt-panel-x' : 'rpt-filters-btn');
  });
  register('rpt-group-toggle', (el) => {
    const dim = el.getAttribute('data-dim');
    if (!dim) return;
    if (_openGroups.has(dim)) _openGroups.delete(dim);
    else _openGroups.add(dim);
    render();
    focusById('rpt-grouph-' + dim);
  });
  // Applied-chip body → open the panel scrolled to and expanded on that dim's
  // group (repurposed from the old inline-dropdown open).
  register('rpt-menu', (el) => {
    const dim = el.getAttribute('data-dim');
    if (!dim) return;
    _panelOpen = true;
    _periodOpen = false;
    _openGroups.add(dim);
    render();
    const dd = doc();
    const body = dd && dd.getElementById('rpt-group-' + dim);
    if (body && typeof body.scrollIntoView === 'function') body.scrollIntoView({ block: 'nearest' });
    focusById('rpt-grouph-' + dim);
  });
  register('rpt-slice', (el) => {
    const dim = el.getAttribute('data-dim');
    const val = el.getAttribute('data-val');
    if (!dim || val === null) return;
    const arr = _filters.slice[dim] || (_filters.slice[dim] = []);
    const i = arr.indexOf(val);
    if (i >= 0) arr.splice(i, 1);
    else arr.push(val);
    // render() replaces the panel wholesale, so a selection made inside an open
    // group would drop focus to <body> — return it to the dim's listbox so
    // multi-select keeps working by keyboard, mirroring the old inline behaviour.
    render();
    _activeOpt[dim] = val;
    const list = listOf(dim);
    if (list) {
      list.setAttribute('aria-activedescendant', 'rpt-opt-' + dim + '-' + optSlug(val));
      list.focus();
    }
  });
  register('rpt-chip-remove', (el) => {
    const dim = el.getAttribute('data-dim');
    if (!dim) return;
    _filters.slice[dim] = [];
    render();
    focusById('rpt-filters-btn');
  });
  register('rpt-clear', () => {
    const tier = _filters.tier; // a display preference, not a filter — it survives
    _filters = Object.assign(defaultFilters(), { tier, slice: emptySlices() });
    _periodOpen = false;
    _panelOpen = false;
    _openGroups = new Set();
    _activeOpt = {};
    render();
  });
  register('rpt-caveat', (el) => {
    const id = el.getAttribute('data-metric');
    const m = id && getMetric(id);
    if (!m) return;
    // The title attribute serves the mouse; this serves touch and keyboard, where
    // there is no hover — the definition must be reachable by every user.
    toast(m.label + ' — ' + m.caveat);
  });
  register('rpt-open-case', (el) => {
    const id = el.getAttribute('data-case');
    if (id) void openWorkspace(id);
  });
  register('rpt-csv', () => onExportCsv());
  register('rpt-ppt', () => void onExportDeck());

  const d = doc();
  if (d) {
    d.addEventListener('change', (/** @type {any} */ e) => {
      const t = e.target;
      if (!t) return;
      if (t.classList && t.classList.contains('rpt-deck')) {
        const id = t.getAttribute('data-metric');
        if (!id) return;
        if (t.checked) _deck.add(id);
        else _deck.delete(id);
        const n = d.getElementById('rpt-deckn');
        if (n) n.textContent = fmtNum(_deck.size);
        const hint = d.getElementById('rpt-deckhint');
        if (hint) {
          hint.textContent = _deck.size ? 'Only these tiles go on the deck' : 'Tick nothing and the deck carries everything';
        }
        return;
      }
      if (t.id === 'rpt-from' || t.id === 'rpt-to') {
        // Store the typed value but do NOT render — the Apply button commits the
        // custom range. Rendering per keystroke rebuilt the popover and stole
        // focus from the date field mid-entry.
        _filters.custom = Object.assign({}, _filters.custom, t.id === 'rpt-from' ? { from: t.value } : { to: t.value });
      }
    });
    // Pivot keyboard model (WAI-ARIA tabs): the arrow keys move BETWEEN tabs with
    // wraparound, Home/End jump to the ends, and — because the panel swap is a
    // cheap local repaint with data already loaded — activation is AUTOMATIC, so
    // the shown area follows the arrow. Runs regardless of any open slice menu.
    d.addEventListener('keydown', (/** @type {any} */ e) => {
      const t = e.target;
      if (!t || !t.classList || !t.classList.contains('rpt-tab')) return;
      // Horizontal tablist (aria-orientation="horizontal"): Left/Right move, Home/
      // End jump. Up/Down are deliberately NOT handled — the announced orientation
      // promises they do something else (leave the tablist), so hijacking them
      // would contradict it.
      const NAV = ['ArrowRight', 'ArrowLeft', 'Home', 'End'];
      if (NAV.indexOf(e.key) < 0) return;
      e.preventDefault();
      const keys = REPORT_AREA_KEYS;
      const n = keys.length;
      let i = keys.indexOf(_area);
      if (i < 0) i = 0;
      if (e.key === 'Home') i = 0;
      else if (e.key === 'End') i = n - 1;
      else if (e.key === 'ArrowRight') i = (i + 1) % n;
      else i = (i - 1 + n) % n;
      switchArea(keys[i]);
    });
    // Radiogroup roving focus (Period popover + panel Clock). The arrows MOVE
    // focus only — they do not select, because selecting has a side effect (the
    // Period popover closes, the Clock re-renders); Enter/Space on the focused
    // radio activates it via the native button. Mirrors the WAI-ARIA radio pattern
    // for radios whose activation is deferred.
    d.addEventListener('keydown', (/** @type {any} */ e) => {
      const t = e.target;
      if (!t || typeof t.getAttribute !== 'function' || t.getAttribute('role') !== 'radio') return;
      const NAV = ['ArrowDown', 'ArrowRight', 'ArrowUp', 'ArrowLeft', 'Home', 'End'];
      if (NAV.indexOf(e.key) < 0) return;
      const group = typeof t.closest === 'function' ? t.closest('[role="radiogroup"]') : null;
      if (!group) return;
      const radios = Array.from(group.querySelectorAll('[role="radio"]'));
      if (!radios.length) return;
      let i = radios.indexOf(t);
      e.preventDefault();
      if (e.key === 'Home') i = 0;
      else if (e.key === 'End') i = radios.length - 1;
      else if (e.key === 'ArrowDown' || e.key === 'ArrowRight') i = (i + 1) % radios.length;
      else i = (i - 1 + radios.length) % radios.length;
      const next = /** @type {any} */ (radios[i]);
      if (next && typeof next.focus === 'function') next.focus();
    });
    // A-19: .rpt-scrim is position:fixed inset:0, so the pointer cannot reach the
    // report behind an open filters panel -- but Tab could, and did: a keyboard or
    // screen-reader user walked the charts, the export buttons and the pivot tabs
    // under the scrim while the panel sat on top claiming role="dialog".
    //
    // Capture phase, and _panelOpen is read at EVENT time rather than latched when
    // the panel opens. The flag is cleared in six places (both toggles, rpt-clear,
    // switchArea, the error/skeleton branches of render(), _resetReportsView); a
    // trap each of those had to tear down would eventually outlive its panel.
    // Nothing is installed per-open, so nothing can leak.
    d.addEventListener(
      'keydown',
      (/** @type {any} */ e) => {
        if (e.key !== 'Tab' || !_panelOpen) return;
        // A modal dialog above owns the trap -- and marks #app inert, which
        // includes this panel. Two traps calling preventDefault would pin focus.
        const ov = d.getElementById('ov');
        if (ov && ov.classList && ov.classList.contains('show')) return;
        const panel = /** @type {any} */ (d.getElementById('rpt-panel-filters'));
        if (!panel) return;
        const ae = /** @type {any} */ (d.activeElement);
        const items = [...panel.querySelectorAll(FOCUSABLE)].filter(
          (el) => /** @type {any} */ (el).offsetParent !== null
        );
        const step = panelTabStep(true, e.shiftKey, panel.contains(ae) ? items.indexOf(ae) : -1, items.length);
        if (step === null) return;
        e.preventDefault();
        const target = /** @type {any} */ (step === -2 ? panel : items[step]);
        if (target && typeof target.focus === 'function') target.focus();
      },
      true
    );
    // The period popover closes on any click outside it and its trigger. (The
    // filters panel has a scrim whose own data-action closes it, so it needs no
    // handler here.)
    d.addEventListener('click', (/** @type {any} */ e) => {
      if (!_periodOpen) return;
      const t = e.target;
      if (!t || typeof t.closest !== 'function') return;
      if (t.closest('.rpt-period-pop') || t.closest('[data-action="rpt-period-open"]')) return;
      // Route through closeSurface so focus returns to the Period button (matching
      // the Escape path), never dropping to <body>.
      closeSurface();
    });
    d.addEventListener('keydown', (/** @type {any} */ e) => {
      // Escape closes whichever filter surface is open, returning focus to its
      // trigger (closeSurface mirrors the old closeMenu discipline).
      if (e.key === 'Escape') {
        if (_panelOpen || _periodOpen) {
          e.preventDefault();
          closeSurface();
        }
        return;
      }
      // Listbox arrow navigation. Keyed off the FOCUSED listbox (not a single
      // open-menu id), so several accordion groups can be open at once and the
      // arrows still move within whichever one holds focus. Enter/Space toggles
      // via the existing data-kbd-click path on the active option.
      const ae = d.activeElement;
      const list = ae && typeof ae.closest === 'function' ? ae.closest('.rpt-menu-list') : null;
      if (!list) return;
      const dim = list.getAttribute('data-dim');
      if (!dim) return;
      const NAV = ['ArrowDown', 'ArrowUp', 'Home', 'End'];
      if (NAV.indexOf(e.key) < 0 && e.key !== 'Enter' && e.key !== ' ') return;
      const opts = Array.from(list.querySelectorAll('.rpt-opt'));
      if (!opts.length) return;
      const vals = opts.map((/** @type {any} */ o) => o.getAttribute('data-val'));
      let i = vals.indexOf(_activeOpt[dim]);
      if (e.key === 'Enter' || e.key === ' ') {
        if (i < 0) return;
        e.preventDefault();
        /** @type {any} */ (opts[i]).click();
        return;
      }
      e.preventDefault();
      if (e.key === 'Home') i = 0;
      else if (e.key === 'End') i = opts.length - 1;
      else if (e.key === 'ArrowDown') i = i < 0 ? 0 : Math.min(opts.length - 1, i + 1);
      else i = i < 0 ? 0 : Math.max(0, i - 1);
      _activeOpt[dim] = vals[i];
      list.setAttribute('aria-activedescendant', /** @type {any} */ (opts[i]).id);
      for (const o of opts) /** @type {any} */ (o).classList.toggle('active', o === opts[i]);
      if (typeof (/** @type {any} */ (opts[i]).scrollIntoView) === 'function') {
        /** @type {any} */ (opts[i]).scrollIntoView({ block: 'nearest' });
      }
    });
  }

  // The shared cases slice is half the snapshot; a Board/Registry reload or a
  // realtime change must re-render Reports off the same rows, never a second read.
  store.subscribe(SLICES.REPORTS, () => {
    if (mounted()) render();
  });
  store.subscribe(SLICES.FILTERS, () => {
    if (mounted()) void refresh();
  });
  store.subscribe(SLICES.RT_CHANGES, (v) => {
    if (mounted() && v && Array.isArray(v.changed)) void refresh();
  });
}

// ─── Registration ──────────────────────────────────────────────────────────

/** Test-only: reset view state between runs. @returns {void} */
export function _resetReportsView() {
  _filters = defaultFilters();
  _deck = new Set();
  _panelOpen = false;
  _periodOpen = false;
  _openGroups = new Set();
  _activeOpt = {};
  _area = 'overview';
  _chartsWarned = false;
  destroyCharts();
}

/** The active pivot area (read-only use — tests). @returns {string} */
export function currentArea() {
  return _area;
}

/** The current filter state (read-only use — tests + the Phase-5B deck). @returns {any} */
export function currentFilters() {
  return _filters;
}

/** The tile ids ticked for the deck. @returns {string[]} */
export function deckSelection() {
  return [..._deck];
}

/**
 * Register the Reports view, replacing the Phase-1 placeholder. Call once at boot
 * from main.js (and the dev harness).
 * @returns {void}
 */
export function registerReports() {
  registerView('reports', {
    render(host) {
      // Pick the active area from the hash BEFORE painting, so a pasted deep-link
      // (#/reports/access) or a browser back/forward between areas lands on the
      // right tab. A genuine hash navigation flows through main.js onRoute, which
      // re-renders the view; a tab click uses replaceState and never gets here.
      _area = reportArea(hashNow());
      host.innerHTML = skeleton();
      wireOnce();
      render(); // paint from whatever is already in the store
      // On a deep-link to a later tab (#/reports/operations) the mobile pivot is a
      // horizontal snap-scroller that starts at the left, so the active tab can be
      // off-screen with no visible active-state. A tab CLICK self-corrects (focus
      // scrolls it in); the initial paint has no focus move, so nudge it here.
      const dd = doc();
      const active = dd && dd.getElementById('rpt-tab-' + _area);
      if (active && typeof active.scrollIntoView === 'function') {
        active.scrollIntoView({ inline: 'nearest', block: 'nearest' });
      }
    },
    load() {
      _chartsWarned = false; // a fresh visit may warn once again
      return refresh();
    },
  });
}

// Referenced for the type-checker's benefit (the GOOD vocabulary is part of the
// registry contract this view renders against).
export { GOOD };
